import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { 
  collection, query, onSnapshot, orderBy, doc, getDoc, getDocs, updateDoc, setDoc, serverTimestamp, addDoc, limit, where, increment, runTransaction, getCountFromServer 
} from 'firebase/firestore';

// ===============================================
// Local Storage Cache Helpers & API
// ===============================================
function getCachedData(key) {
  try { return JSON.parse(localStorage.getItem(key)) || []; } catch(e) { return []; }
}
function getCachedObj(key) {
  try { return JSON.parse(localStorage.getItem(key)) || {}; } catch(e) { return {}; }
}
function getLocalContacts() {
  try { return JSON.parse(localStorage.getItem('nexora_local_contacts') || '{}'); } catch (e) { return {}; }
}
function saveLocalContact(phoneKey, name) {
  try {
    const contacts = getLocalContacts();
    contacts[phoneKey] = name;
    localStorage.setItem('nexora_local_contacts', JSON.stringify(contacts));
  } catch (e) {}
}

function toMillisSafe(value) {
  if (!value) return 0;
  try {
    if (typeof value === 'number') return value;
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (typeof value.seconds === 'number') return value.seconds * 1000;
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  } catch (e) {
    return 0;
  }
}

function durationToMsSafe(value, unit) {
  const n = Math.max(1, Number(value) || 1);
  const map = {
    seconds: 1000,
    minutes: 60 * 1000,
    hours: 60 * 60 * 1000,
    days: 24 * 60 * 60 * 1000,
  };
  return n * (map[unit] || map.seconds);
}

function normalizeOfficialDriveUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  const match = raw.match(/(?:id=|\/d\/)([a-zA-Z0-9_-]{10,})/);
  if (match?.[1] && /drive\.google\.com|drive\.usercontent\.google\.com/i.test(raw)) {
    return `https://drive.usercontent.google.com/download?id=${match[1]}&export=download&confirm=t`;
  }
  return raw;
}

function getChatCache(roomId) {
  try {
    if (!roomId) return { messages: [], unreadIds: [], unreadCount: 0, savedAt: 0 };
    const raw = localStorage.getItem(`nexora_p2p_msgs_${roomId}`);
    if (!raw) return { messages: [], unreadIds: [], unreadCount: 0, savedAt: 0 };

    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return { messages: parsed, unreadIds: [], unreadCount: 0, savedAt: 0 };
    }
    return {
      messages: Array.isArray(parsed.messages) ? parsed.messages : [],
      unreadIds: Array.isArray(parsed.unreadIds) ? parsed.unreadIds : [],
      unreadCount: Number(parsed.unreadCount) || 0,
      savedAt: Number(parsed.savedAt) || 0
    };
  } catch (e) {
    return { messages: [], unreadIds: [], unreadCount: 0, savedAt: 0 };
  }
}

function saveChatCache(roomId, messages, unreadIds = [], unreadCount = 0, hasBlockedTarget = false, isBlockedByTarget = false) {
  try {
    if (!roomId || !Array.isArray(messages)) return;

    const cachedMessages = messages.length > 100 ? messages.slice(-100) : messages;

    localStorage.setItem(
      `nexora_p2p_msgs_${roomId}`,
      JSON.stringify({
        messages: cachedMessages,
        unreadIds: Array.isArray(unreadIds) ? unreadIds.slice(-50) : [],
        unreadCount: Number(unreadCount) || 0,
        savedAt: Date.now(),
        // নতুন ডেটা যোগ করা হলো
        hasBlockedTarget: hasBlockedTarget,
        isBlockedByTarget: isBlockedByTarget
      })
    );
  } catch (e) {
    console.warn('Chat cache save skipped:', e);
  }
}

// ================================================================
// IndexedDB cache: Dashboard owns background preload/synchronization.
// localStorage remains only as a compatibility mirror for the current
// ChatScreen/GroupScreen until those screens are moved to IndexedDB.
// ================================================================
const NEXORA_CACHE_DB = 'nexora_cache_v2';
const NEXORA_CACHE_DB_VERSION = 1;
let nexoraCacheDbPromise = null;

function openNexoraCacheDB() {
  if (typeof window === 'undefined' || !('indexedDB' in window)) return Promise.resolve(null);
  if (nexoraCacheDbPromise) return nexoraCacheDbPromise;
  nexoraCacheDbPromise = new Promise((resolve) => {
    try {
      const request = indexedDB.open(NEXORA_CACHE_DB, NEXORA_CACHE_DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains('chatPreloads')) db.createObjectStore('chatPreloads');
        if (!db.objectStoreNames.contains('groupPreloads')) db.createObjectStore('groupPreloads');
        if (!db.objectStoreNames.contains('profiles')) db.createObjectStore('profiles');
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
    } catch (e) { resolve(null); }
  });
  return nexoraCacheDbPromise;
}

function idbPut(storeName, key, value) {
  return openNexoraCacheDB().then(db => new Promise(resolve => {
    if (!db) return resolve(false);
    try {
      const tx = db.transaction(storeName, 'readwrite');
      tx.objectStore(storeName).put(value, key);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
      tx.onabort = () => resolve(false);
    } catch (e) { resolve(false); }
  }));
}

function idbDelete(storeName, key) {
  return openNexoraCacheDB().then(db => new Promise(resolve => {
    if (!db) return resolve(false);
    try {
      const tx = db.transaction(storeName, 'readwrite');
      tx.objectStore(storeName).delete(key);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    } catch (e) { resolve(false); }
  }));
}

function saveChatPreloadIndexed(roomId, cache) {
  if (!roomId || !cache) return Promise.resolve(false);
  return idbPut('chatPreloads', roomId, {
    messages: Array.isArray(cache.messages) ? cache.messages : [],
    unreadIds: Array.isArray(cache.unreadIds) ? cache.unreadIds : [],
    unreadCount: Number(cache.unreadCount) || 0,
    firstUnreadId: cache.unreadIds?.[0] || null,
    savedAt: Date.now()
  });
}

function saveGroupPreloadIndexed(groupId, cache) {
  if (!groupId || !cache) return Promise.resolve(false);
  return idbPut('groupPreloads', groupId, {
    messages: Array.isArray(cache.messages) ? cache.messages : [],
    unreadIds: Array.isArray(cache.unreadIds) ? cache.unreadIds : [],
    unreadCount: Number(cache.unreadCount) || 0,
    firstUnreadId: cache.unreadIds?.[0] || null,
    savedAt: Date.now()
  });
}

const IMGBB_API_KEY = "af3aa5305ed243a9489b195b87a2afe5"; 
const OFFICIAL_POST_CACHE_KEY = 'nexora_official_posts_cache';
const uploadToImgBB = async (file) => {
  const formData = new FormData();
  formData.append('image', file);
  try {
    const res = await fetch(`https://api.imgbb.com/1/upload?key=${IMGBB_API_KEY}`, { method: 'POST', body: formData });
    const data = await res.json();
    return data.data?.url || null;
  } catch (e) { return null; }
};

const compressImage = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width; let height = img.height; const maxWidth = 1000;
        if (width > maxWidth) { height = Math.round((height * maxWidth) / width); width = maxWidth; }
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.7));
      };
    };
  });
};

const Dashboard = () => {
  const navigate = useNavigate();
  
  const [currentUid, setCurrentUid] = useState(localStorage.getItem('nexora_user_uid') || null);
  const [currentUserData, setCurrentUserData] = useState(getCachedObj('nexora_current_user'));
  const [activeTab, setActiveTab] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  // ফিড ক্যাশ
  const [recentChats, setMessagesFeed] = useState(getCachedData('nexora_dash_chats'));
  const [allUsers, setAllUsers] = useState(getCachedData('nexora_dash_users'));
  const [userProfilesMap, setUserProfilesMap] = useState(getCachedObj('nexora_dash_users_map'));
  const [communities, setCommunities] = useState(getCachedData('nexora_dash_comms'));
  const [groups, setGroups] = useState(getCachedData('nexora_dash_groups'));
  
  const [pinnedIds, setPinnedIds] = useState([]);
  const [favouriteIds, setFavouriteIds] = useState([]);
  const dismissedDashboardRef = useRef({});
  // Prevent duplicate background message-cache reads while the Dashboard
  // chat listener is refreshing.
  const chatCachePrefetchInFlightRef = useRef(new Set());
  const communityPreloadMemoryRef = useRef(new Map());
  const communityPrefetchInFlightRef = useRef(new Set());
  const groupSeenSyncHandlerRef = useRef(null);
  const chatPreloadMemoryRef = useRef(new Map());
  const groupPreloadMemoryRef = useRef(new Map());
  const profileFetchesRef = useRef(new Set());

  // কাস্টম লিস্ট ও মাল্টি-সিলেক্ট স্টেট
  const [customTabs, setCustomTabs] = useState(getCachedData(`nexora_custom_tabs_${currentUid}`) || []);
  const [customListsMap, setCustomListsMap] = useState(getCachedObj(`nexora_custom_lists_map_${currentUid}`) || {});
  const [selectedItems, setSelectedItems] = useState([]);
  const [showListSelectorModal, setShowListSelectorModal] = useState(false);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('nexoraDashboardSelection', {
      detail: { active: selectedItems.length > 0 }
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('nexoraDashboardSelection', {
        detail: { active: false }
      }));
    };
  }, [selectedItems.length]);

  // বাল্ক মেসেজ (SMS) স্টেট
  const [showBulkSmsModal, setShowBulkSmsModal] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  // ভয়েস রেকর্ডার স্টেট (বাল্ক মেসেজের জন্য)
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [waveBars, setWaveBars] = useState([12, 22, 35, 18, 30, 10, 28, 40, 15, 18]);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animFrameRef = useRef(null);

  const bulkTextareaRef = useRef(null);
  const bulkMediaInputRef = useRef(null);
  const bulkFileInputRef = useRef(null);

  const clickCountRef = useRef({});
  const clickTimerRef = useRef({});
  const tabLongPressTimerRef = useRef(null);

  const [showContactModal, setShowContactModal] = useState(false);
  const [newManualPhone, setNewManualPhone] = useState('');
  const [newManualName, setNewManualName] = useState('');
  const [localContacts, setLocalContacts] = useState(getLocalContacts());
  const [connectionContacts, setConnectionContacts] = useState({});
  const [dashboardStories, setDashboardStories] = useState([]);

  const [officialPosts, setOfficialPosts] = useState(() => {
    const now = Date.now();
    return getCachedData(OFFICIAL_POST_CACHE_KEY).filter(post => {
      const expiresAt = toMillisSafe(post?.expiresAt);
      return !expiresAt || expiresAt > now || post?.placement === 'popup';
    });
  });
  const [officialPopupPost, setOfficialPopupPost] = useState(null);
  const [showOfficialPopup, setShowOfficialPopup] = useState(false);
  const [officialBannerPost, setOfficialBannerPost] = useState(null);
  const officialPopupTimerRef = useRef(null);
  const officialPopupHideTimerRef = useRef(null);
  const officialBannerExpiryTimerRef = useRef(null);
  const officialViewedRef = useRef(new Set());

  const saveOfficialPostsCache = (posts) => {
    try {
      localStorage.setItem(OFFICIAL_POST_CACHE_KEY, JSON.stringify(posts));
    } catch (error) {
      console.debug('Official post cache save skipped:', error?.message || error);
    }
  };

  const normalizePhone = (p) => {
    if (!p) return '';
    let d = p.replace(/\D/g, '');
    if (d.startsWith('880')) d = d.substring(3);
    if (d.startsWith('0')) d = d.substring(1);
    return `+880${d}`;
  };

  const handleNavigate = (path, state = {}) => {
    navigate(path, state);
  };

  useEffect(() => {
    const handlePopState = () => { if (showContactModal) setShowContactModal(false); };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [showContactModal]);

  const openContactModal = () => { window.history.pushState({ modal: 'contact' }, ''); setShowContactModal(true); };
  const closeContactModal = () => { if (window.history.state?.modal === 'contact') { window.history.back(); } else { setShowContactModal(false); } };

  useEffect(() => {
    const userUid = localStorage.getItem('nexora_user_uid') || auth.currentUser?.uid;
    if (!userUid) { navigate('/login'); return; }

    // GroupScreen dispatches this event immediately after it successfully
    // marks the current user's group messages as seen. Update Dashboard
    // optimistically so the unread badge disappears without waiting for the
    // next Firestore snapshot.
    const handleGroupSeenSync = (event) => {
      const { groupId, unreadCount } = event.detail || {};
      if (!groupId) return;

      const safeCount = Number(unreadCount) || 0;

      setGroups(prev => {
        const next = prev.map(item =>
          item.id === groupId
            ? { ...item, unreadCount: { ...(item.unreadCount || {}), [userUid]: safeCount } }
            : item
        );
        try { localStorage.setItem('nexora_dash_groups', JSON.stringify(next)); } catch (e) {}
        return next;
      });
    };

    groupSeenSyncHandlerRef.current = handleGroupSeenSync;
    window.addEventListener('nexoraGroupSeenSync', handleGroupSeenSync);
    setCurrentUid(userUid);

    const savedPins = JSON.parse(localStorage.getItem(`nexora_pins_${userUid}`) || '[]');
    const savedFavs = JSON.parse(localStorage.getItem(`nexora_favs_${userUid}`) || '[]');
    const savedTabs = JSON.parse(localStorage.getItem(`nexora_custom_tabs_${userUid}`) || '[]');
    const savedListsMap = JSON.parse(localStorage.getItem(`nexora_custom_lists_map_${userUid}`) || '{}');
    
    setPinnedIds(savedPins); setFavouriteIds(savedFavs);
    setCustomTabs(savedTabs); setCustomListsMap(savedListsMap);

    const userDocRef = doc(db, 'users', userUid);
    updateDoc(userDocRef, { isOnline: true, lastSeen: serverTimestamp() }).catch(() => {});
    const handleVisibilityChange = () => {
      if (document.hidden) updateDoc(userDocRef, { isOnline: false, lastSeen: serverTimestamp() }).catch(() => {});
      else updateDoc(userDocRef, { isOnline: true, lastSeen: serverTimestamp() }).catch(() => {});
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    getDoc(userDocRef).then(snap => {
      if (snap.exists()) {
        setCurrentUserData(snap.data()); localStorage.setItem('nexora_current_user', JSON.stringify(snap.data()));
      }
    });

    // Never subscribe to the whole users collection on Dashboard refresh.
    // Existing profile cache is used first; missing profiles are fetched only
    // when a chat/contract actually needs them.
    const unsubUsers = () => {};
    const cachedProfileMap = getCachedObj(`nexora_dash_users_map_${userUid}`);
    if (Object.keys(cachedProfileMap).length) setUserProfilesMap(cachedProfileMap);
    const cachedDirectory = getCachedData(`nexora_dash_users_${userUid}`);
    if (cachedDirectory.length) setAllUsers(cachedDirectory);

    const connectionDocRef = doc(db, 'connections', userUid);
    const unsubConnections = onSnapshot(connectionDocRef, (snap) => {
      const data = snap.exists() ? (snap.data() || {}) : {};
      const contacts = data.contacts && typeof data.contacts === 'object' ? data.contacts : {};
      setConnectionContacts(contacts);
    }, () => {
      setConnectionContacts({});
    });

    const existingLocal = getLocalContacts();
    if (Object.keys(existingLocal).length > 0) {
      const migratedContacts = {};
      Object.entries(existingLocal).forEach(([phone, name]) => {
        if (phone && name) migratedContacts[phone] = { name, phoneNumber: phone };
      });
      setDoc(connectionDocRef, { contacts: migratedContacts }, { merge: true }).catch(() => {});
    }

    const chatsQuery = query(collection(db, 'chats'), where('participants', 'array-contains', userUid));
    const unsubChats = onSnapshot(chatsQuery, (snapshot) => {
      const feed = [];

      snapshot.forEach(docSnap => {
        const data = docSnap.data();

        if (data.participants && data.participants.includes(userUid)) {
          const otherUid = data.participants.find(id => id !== userUid) || userUid;
          const dismissedAt = dismissedDashboardRef.current[docSnap.id];
          const updatedAtMillis = data.updatedAt?.toMillis?.() || (data.updatedAt?.seconds ? data.updatedAt.seconds * 1000 : 0);

          if (dismissedAt && updatedAtMillis <= dismissedAt) return;
          if (dismissedAt && updatedAtMillis > dismissedAt) delete dismissedDashboardRef.current[docSnap.id];

          feed.push({ id: docSnap.id, otherUid, ...data });

          // Load only a missing chat profile. Never scan the users collection.
          const cachedProfileMapNow = getCachedObj(`nexora_dash_users_map_${userUid}`);
          if (otherUid && otherUid !== userUid && !cachedProfileMapNow[otherUid] && !profileFetchesRef.current.has(otherUid)) {
            profileFetchesRef.current.add(otherUid);
            getDoc(doc(db, 'users', otherUid)).then(profileSnap => {
              if (!profileSnap.exists()) return;
              const profile = { id: profileSnap.id, ...profileSnap.data() };
              setUserProfilesMap(prev => {
                const next = { ...prev, [otherUid]: profile };
                if (profile.phoneNumber) next[normalizePhone(profile.phoneNumber)] = profile;
                try {
                  localStorage.setItem(`nexora_dash_users_map_${userUid}`, JSON.stringify(next));
                  localStorage.setItem('nexora_dash_users_map', JSON.stringify(next));
                } catch (e) {}
                idbPut('profiles', otherUid, profile);
                return next;
              });
            }).catch(() => {}).finally(() => profileFetchesRef.current.delete(otherUid));
          }

          // ---------------------------------------------------------
          // Dashboard-owned P2P message cache preloader
          // ---------------------------------------------------------
          // The Dashboard already knows the chat's unreadCount. When a
          // chat has new unread messages, fetch the newest small window
          // here and save the complete message objects (including
          // senderName/senderPhoto) into the same cache ChatScreen uses.
          //
          // This is intentionally background-only: it never blocks the
          // Dashboard UI and never waits for ChatScreen to open.
          const unreadForMe = Number(data.unreadCount?.[userUid] || 0);
          const cached = getChatCache(docSnap.id);
          const cachedUnread = Number(cached.unreadCount || 0);

          if (
            unreadForMe > 0 &&
            (unreadForMe > cachedUnread || cached.messages.length === 0) &&
            !chatCachePrefetchInFlightRef.current.has(docSnap.id)
          ) {
            chatCachePrefetchInFlightRef.current.add(docSnap.id);

            const messagesRef = collection(db, 'chats', docSnap.id, 'messages');
            const prefetchLimit = Math.min(
              100,
              Math.max(30, unreadForMe + 10)
            );

            getDocs(
              query(messagesRef, orderBy('createdAt', 'desc'), limit(prefetchLimit))
            ).then(messageSnapshot => {
              const fetched = [];
              messageSnapshot.forEach(messageDoc => {
                fetched.push({ id: messageDoc.id, ...messageDoc.data() });
              });
              fetched.reverse();

              // Keep any existing cached history and merge new messages.
              const map = new Map();
              (cached.messages || []).forEach(m => map.set(m.id, m));
              fetched.forEach(m => map.set(m.id, m));

              const merged = Array.from(map.values()).sort((a, b) => {
                const getMillis = (value) => {
                  if (!value) return 0;
                  if (typeof value === 'number') return value;
                  if (typeof value.toMillis === 'function') return value.toMillis();
                  if (typeof value.seconds === 'number') return value.seconds * 1000;
                  const parsed = new Date(value).getTime();
                  return Number.isFinite(parsed) ? parsed : 0;
                };
                return getMillis(a.createdAt) - getMillis(b.createdAt);
              });

              const unreadMessages = fetched
                .filter(m => m.senderId !== userUid && m.status !== 'seen')
                .sort((a, b) => {
                  const getMillis = (value) => {
                    if (!value) return 0;
                    if (typeof value === 'number') return value;
                    if (typeof value.toMillis === 'function') return value.toMillis();
                    if (typeof value.seconds === 'number') return value.seconds * 1000;
                    const parsed = new Date(value).getTime();
                    return Number.isFinite(parsed) ? parsed : 0;
                  };
                  return getMillis(a.createdAt) - getMillis(b.createdAt);
                });

              const unreadIds = unreadMessages.map(m => m.id);

              // If the room says there are more unread messages than the
              // latest window contained, preserve the dashboard count.
              const preloadCache = {
                messages: merged,
                unreadIds: unreadIds.length > 0 ? unreadIds : cached.unreadIds,
                unreadCount: unreadForMe
              };
              chatPreloadMemoryRef.current.set(docSnap.id, preloadCache);
              saveChatPreloadIndexed(docSnap.id, preloadCache);
              // Compatibility mirror for the current ChatScreen.
              saveChatCache(docSnap.id, merged, preloadCache.unreadIds, unreadForMe);
            }).catch(() => {
              // Cache preloading is best-effort; Dashboard must continue normally.
            }).finally(() => {
              chatCachePrefetchInFlightRef.current.delete(docSnap.id);
            });
          }

          // If the room is now read, immediately reset the cached unread
          // metadata without deleting the already cached message history.
          if (unreadForMe === 0) {
            if (cached.unreadCount > 0 || cached.unreadIds?.length) {
              saveChatCache(docSnap.id, cached.messages, [], 0);
            }
            chatPreloadMemoryRef.current.delete(docSnap.id);
            idbDelete('chatPreloads', docSnap.id);
          }
        }
      });

      setMessagesFeed(feed);
      localStorage.setItem('nexora_dash_chats', JSON.stringify(feed));
    });

    const communityPrefetchInFlight = communityPrefetchInFlightRef.current;

    const unsubComm = onSnapshot(collection(db, 'communities'), (snapshot) => {
      const comms = [];

      snapshot.forEach(d => {
        const cData = d.data();
        const isMember = cData.adminUid === userUid || (cData.members && cData.members.includes(userUid));
        if (!isMember) return;

        const dismissedAt = dismissedDashboardRef.current[d.id];
        const updatedAtMillis = cData.updatedAt?.toMillis?.() || (cData.updatedAt?.seconds ? cData.updatedAt.seconds * 1000 : 0);
        if (dismissedAt && updatedAtMillis <= dismissedAt) return;

        if (dismissedAt && updatedAtMillis > dismissedAt) {
          delete dismissedDashboardRef.current[d.id];
          try {
            localStorage.setItem(
              `nexora_dashboard_dismissed_${userUid}`,
              JSON.stringify(dismissedDashboardRef.current)
            );
          } catch (e) {}
        }

        const communityItem = { id: d.id, isCommunity: true, ...cData };
        comms.push(communityItem);

        // COMMUNITY POST CACHE PRELOAD
        // Prepare the exact cache CommunityScreen consumes before the user opens it.
        const unreadForMe = Number(cData.unreadCount?.[userUid] || 0);
        const cacheKey = `nexora_comm_msgs_${d.id}`;

        let cached = { messages: [], unreadIds: [], firstUnreadId: null, unreadCount: 0 };
        try {
          const raw = localStorage.getItem(cacheKey);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
              cached.messages = parsed;
            } else if (parsed && typeof parsed === 'object') {
              cached = {
                messages: Array.isArray(parsed.messages) ? parsed.messages : [],
                unreadIds: Array.isArray(parsed.unreadIds) ? parsed.unreadIds : [],
                firstUnreadId: parsed.firstUnreadId || null,
                unreadCount: Number(parsed.unreadCount) || 0
              };
            }
          }
        } catch (e) {}

        if (unreadForMe > 0 && !communityPrefetchInFlight.has(d.id)) {
          communityPrefetchInFlight.add(d.id);

          const postsRef = collection(db, 'communities', d.id, 'posts');
          const prefetchQ = query(
            postsRef,
            orderBy('createdAt', 'desc'),
            limit(Math.min(100, Math.max(30, unreadForMe + 10)))
          );

          getDocs(prefetchQ).then(postSnapshot => {
            const fetched = [];
            postSnapshot.forEach(postDoc => {
              fetched.push({ id: postDoc.id, ...postDoc.data() });
            });
            fetched.reverse();

            const map = new Map();
            (cached.messages || []).forEach(m => map.set(m.id, m));
            fetched.forEach(m => map.set(m.id, m));

            const merged = Array.from(map.values()).sort((a, b) => {
              const tm = v => v?.toMillis
                ? v.toMillis()
                : (v?.seconds ? v.seconds * 1000 : Number(v) || 0);
              return tm(a.createdAt) - tm(b.createdAt);
            });

            const unreadIds = merged
              .filter(m =>
                m.type !== 'system' &&
                m.senderId !== userUid &&
                (!m.seenBy || !m.seenBy[userUid])
              )
              .map(m => m.id);

            const finalMessages = merged.length > 100 ? merged.slice(-100) : merged;
            const firstUnreadId = unreadIds[0] || null;

            const preloadCache = {
              messages: finalMessages,
              unreadIds,
              firstUnreadId,
              unreadCount: unreadForMe,
              savedAt: Date.now()
            };

            communityPreloadMemoryRef.current.set(d.id, preloadCache);

            try {
              localStorage.setItem(cacheKey, JSON.stringify(preloadCache));
            } catch (e) {}

            window.dispatchEvent(new CustomEvent('nexoraCommunityCacheUpdated', {
              detail: {
                communityId: d.id,
                unreadCount: unreadForMe,
                firstUnreadId
              }
            }));
          }).catch(() => {
            // Best effort only. Dashboard list must continue working.
          }).finally(() => {
            communityPrefetchInFlight.delete(d.id);
          });
        } else if (unreadForMe === 0) {
          // Keep message history, but remove this user's unread anchor.
          if (cached.unreadCount > 0 || cached.unreadIds?.length || cached.firstUnreadId) {
            try {
              localStorage.setItem(cacheKey, JSON.stringify({
                ...cached,
                unreadIds: [],
                firstUnreadId: null,
                unreadCount: 0,
                savedAt: Date.now()
              }));
            } catch (e) {}
          }
          communityPreloadMemoryRef.current.delete(d.id);
        }
      });

      setCommunities(comms);
      localStorage.setItem('nexora_dash_comms', JSON.stringify(comms));
    });

    const groupPrefetchInFlight = new Set();

    const unsubGroups = onSnapshot(collection(db, 'groups'), (snapshot) => {
      const grps = [];

      snapshot.forEach(d => {
        const gData = d.data();
        const isMember = gData.adminUid === userUid || (gData.members && gData.members.includes(userUid));
        if (!isMember) return;

        const dismissedAt = dismissedDashboardRef.current[d.id];
        const updatedAtMillis = gData.updatedAt?.toMillis?.() || (gData.updatedAt?.seconds ? gData.updatedAt.seconds * 1000 : 0);
        if (dismissedAt && updatedAtMillis <= dismissedAt) return;
        if (dismissedAt && updatedAtMillis > dismissedAt) delete dismissedDashboardRef.current[d.id];

        grps.push({ id: d.id, isGroup: true, ...gData });

        // ------------------------------------------------------------
        // GROUP CACHE PRELOAD
        // Dashboard already receives the group's unreadCount. Prepare
        // the same cache GroupScreen consumes BEFORE the user opens it.
        // ------------------------------------------------------------
        const unreadForMe = Number(gData.unreadCount?.[userUid] || 0);
        const cacheKey = `nexora_group_msgs_${d.id}`;
        let cached = [];
        try {
          const raw = localStorage.getItem(cacheKey);
          const parsed = raw ? JSON.parse(raw) : [];
          cached = Array.isArray(parsed) ? parsed : (Array.isArray(parsed.messages) ? parsed.messages : []);
        } catch (e) {}

        if (unreadForMe > 0 && !groupPrefetchInFlight.has(d.id)) {
          groupPrefetchInFlight.add(d.id);
          const messagesRef = collection(db, 'groups', d.id, 'messages');
          const prefetchQ = query(messagesRef, orderBy('createdAt', 'desc'), limit(Math.min(100, Math.max(30, unreadForMe + 10))));

          getDocs(prefetchQ).then(messageSnapshot => {
            const fetched = [];
            messageSnapshot.forEach(messageDoc => fetched.push({ id: messageDoc.id, ...messageDoc.data() }));
            fetched.reverse();

            const map = new Map();
            cached.forEach(m => map.set(m.id, m));
            fetched.forEach(m => map.set(m.id, m));
            const merged = Array.from(map.values()).sort((a, b) => {
              const tm = v => v?.toMillis ? v.toMillis() : (v?.seconds ? v.seconds * 1000 : Number(v) || 0);
              return tm(a.createdAt) - tm(b.createdAt);
            });

            const unreadIds = merged
              .filter(m => m.type !== 'system' && m.senderId !== userUid && (!m.seenBy || !m.seenBy[userUid]))
              .map(m => m.id);

            const finalMessages = merged.length > 100 ? merged.slice(-100) : merged;
            const preloadCache = {
              messages: finalMessages,
              unreadIds,
              unreadCount: unreadForMe
            };
            groupPreloadMemoryRef.current.set(d.id, preloadCache);
            saveGroupPreloadIndexed(d.id, preloadCache);
            // Compatibility mirror for the current GroupScreen.
            localStorage.setItem(cacheKey, JSON.stringify({ ...preloadCache, savedAt: Date.now() }));

            window.dispatchEvent(new CustomEvent('nexoraGroupCacheUpdated', {
              detail: { groupId: d.id, unreadCount: unreadForMe }
            }));
          }).catch(() => {}).finally(() => groupPrefetchInFlight.delete(d.id));
        } else if (unreadForMe === 0) {
          // Keep permanent history warm, but remove only the temporary unread preload.
          groupPreloadMemoryRef.current.delete(d.id);
          idbDelete('groupPreloads', d.id);
          if (cached.length > 0) {
            try {
              const raw = localStorage.getItem(cacheKey);
              const parsed = raw ? JSON.parse(raw) : null;
              if (parsed && !Array.isArray(parsed)) {
                localStorage.setItem(cacheKey, JSON.stringify({ ...parsed, unreadIds: [], unreadCount: 0, savedAt: Date.now() }));
              }
            } catch (e) {}
          }
        }
      });

      setGroups(grps);
      localStorage.setItem('nexora_dash_groups', JSON.stringify(grps));
    });

    return () => {
      unsubUsers();
      unsubConnections();
      unsubChats();
      unsubComm();
      unsubGroups();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('nexoraGroupSeenSync', handleGroupSeenSync);
      groupSeenSyncHandlerRef.current = null;
    };
  }, [navigate]);

  useEffect(() => {
    if (!currentUid) return undefined;

    return onSnapshot(collection(db, 'stories'), snapshot => {
      const now = Date.now();
      const myPhone = normalizePhone(currentUserData?.phoneNumber);
      const myIds = new Set([currentUid, currentUserData?.uid, myPhone].filter(Boolean));
      const contactIds = new Set();
      Object.entries({ ...localContacts, ...connectionContacts }).forEach(([phone, value]) => {
        const normalized = normalizePhone(typeof value === 'object' ? value?.phoneNumber || phone : phone);
        const contactUid = typeof value === 'object' ? value?.uid : '';
        if (normalized) contactIds.add(normalized);
        if (contactUid) contactIds.add(contactUid);
      });

      const latestByOwner = new Map();
      snapshot.docs.forEach(storyDoc => {
        const story = { id: storyDoc.id, ...storyDoc.data() };
        if (toMillisSafe(story.expiresAt) <= now) return;
        const isMine = myIds.has(story.ownerUid) || myIds.has(story.userId);
        const isVisible = isMine || !story.privacy || story.privacy === 'public'
          || (story.privacy === 'friends' && (contactIds.has(story.userId) || contactIds.has(story.ownerUid)))
          || (story.privacy === 'selected' && (story.visibleTo || []).some(id => myIds.has(id)));
        if (!isVisible) return;
        const ownerKey = story.ownerUid || story.userId;
        if (!ownerKey) return;
        const previous = latestByOwner.get(ownerKey);
        if (!previous || toMillisSafe(story.createdAt) > toMillisSafe(previous.createdAt)) {
          latestByOwner.set(ownerKey, { ...story, ownerKey, isMine });
        }
      });

      const stories = Array.from(latestByOwner.values()).map(story => ({
        ...story,
        isSeen: (story.viewers || []).some(id => myIds.has(id))
      }));
      stories.sort((a, b) => Number(b.isMine) - Number(a.isMine) || Number(a.isSeen) - Number(b.isSeen) || toMillisSafe(b.createdAt) - toMillisSafe(a.createdAt));
      setDashboardStories(stories);
    }, error => {
      console.warn('Story rail could not be loaded:', error?.message || error);
      setDashboardStories([]);
    });
  }, [currentUid, currentUserData, localContacts, connectionContacts]);

  // =========================================================
  // TARGETED REALTIME USER STATUS
  // Only subscribe to users whose status is actually needed by
  // the Dashboard. Never scan/listen to the whole users collection.
  // =========================================================
  useEffect(() => {
    const userUid = currentUid;
    if (!userUid) return;

    const targetKeys = new Set();

    // P2P cards need the other user's realtime status.
    recentChats.forEach((chat) => {
      const otherUid = chat?.otherUid;
      if (otherUid && otherUid !== userUid) targetKeys.add(`uid:${otherUid}`);
    });

    // Contract entries use the phone-number document id in users/{phone}.
    // Only subscribe to contacts that are already saved locally/connection-wise.
    Object.keys({ ...localContacts, ...connectionContacts }).forEach((phone) => {
      const normalized = normalizePhone(phone);
      if (normalized) targetKeys.add(`phone:${normalized}`);
    });

    if (!targetKeys.size) return;

    const unsubs = [];
    const updateProfile = (profile, key) => {
      if (!profile) return;
      const uidKey = profile.uid || (key.startsWith('uid:') ? key.slice(4) : null);
      const phoneKey = profile.phoneNumber ? normalizePhone(profile.phoneNumber) : (key.startsWith('phone:') ? key.slice(6) : null);

      setUserProfilesMap((prev) => {
        const next = { ...prev };
        if (uidKey) next[uidKey] = { ...(next[uidKey] || {}), ...profile };
        if (phoneKey) next[phoneKey] = { ...(next[phoneKey] || {}), ...profile };
        try {
          localStorage.setItem(`nexora_dash_users_map_${userUid}`, JSON.stringify(next));
          localStorage.setItem('nexora_dash_users_map', JSON.stringify(next));
        } catch (e) {}
        if (uidKey) idbPut('profiles', uidKey, { ...(next[uidKey] || {}), ...profile });
        return next;
      });
    };

    targetKeys.forEach((key) => {
      const value = key.slice(key.indexOf(':') + 1);
      const ref = doc(db, 'users', value);
      const unsub = onSnapshot(ref, (snap) => {
        if (!snap.exists()) return;
        updateProfile({ id: snap.id, ...snap.data() }, key);
      }, () => {});
      unsubs.push(unsub);
    });

    return () => unsubs.forEach((unsub) => { try { unsub(); } catch (e) {} });
  }, [currentUid, recentChats, localContacts, connectionContacts]);

  // =============================================================
  // NEXORA OFFICIALS - live post feed + per-user popup/banner logic
  // =============================================================
  const markOfficialPostViewed = async (post) => {
    if (!currentUid || !post?.id) return;

    const key = `${post.id}:${currentUid}`;
    if (officialViewedRef.current.has(key)) return;
    officialViewedRef.current.add(key);

    try {
      const viewRef = doc(db, 'officialUpdates', post.id, 'views', currentUid);
      const existing = await getDoc(viewRef);
      if (existing.exists()) return;

      await setDoc(viewRef, {
        uid: currentUid,
        viewedAt: serverTimestamp(),
      }, { merge: true });

      await updateDoc(doc(db, 'officialUpdates', post.id), {
        viewCount: increment(1),
      });
    } catch (error) {
      console.debug('Official post view tracking skipped:', error?.message || error);
    }
  };

  useEffect(() => {
    if (!currentUid) return undefined;

    const unsubscribe = onSnapshot(
      collection(db, 'officialUpdates'),
      snapshot => {
        const now = Date.now();
        const list = snapshot.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(post => post.active !== false)
          .filter(post => {
            if (post.placement === 'popup') return true;
            const expiresAt = toMillisSafe(post.expiresAt);
            return !expiresAt || expiresAt > now;
          })
          .sort((a, b) => toMillisSafe(b.createdAt) - toMillisSafe(a.createdAt));

        setOfficialPosts(list);
        saveOfficialPostsCache(list);
      },
      error => console.debug('Official posts listener failed:', error?.message || error)
    );

    return () => unsubscribe();
  }, [currentUid]);

  useEffect(() => {
    if (officialBannerExpiryTimerRef.current) clearTimeout(officialBannerExpiryTimerRef.current);

    const banner = officialPosts.find(post => post.placement === 'banner');
    if (!banner) {
      setOfficialBannerPost(null);
      return undefined;
    }

    const expiresAt = toMillisSafe(banner.expiresAt);
    if (expiresAt && expiresAt <= Date.now()) {
      setOfficialBannerPost(null);
      return undefined;
    }

    setOfficialBannerPost(banner);

    if (expiresAt) {
      officialBannerExpiryTimerRef.current = setTimeout(() => {
        setOfficialBannerPost(null);
        setOfficialPosts(prev => {
          const next = prev.filter(post => post.id !== banner.id);
          saveOfficialPostsCache(next);
          return next;
        });
      }, Math.max(0, expiresAt - Date.now()) + 50);
    }

    return () => {
      if (officialBannerExpiryTimerRef.current) clearTimeout(officialBannerExpiryTimerRef.current);
    };
  }, [officialPosts]);

  useEffect(() => {
    if (!currentUid) return undefined;

    const popup = officialPosts.find(post => post.placement === 'popup');
    if (officialPopupTimerRef.current) clearTimeout(officialPopupTimerRef.current);
    if (officialPopupHideTimerRef.current) clearTimeout(officialPopupHideTimerRef.current);
    setShowOfficialPopup(false);
    setOfficialPopupPost(null);

    if (!popup) return undefined;

    const viewKey = `${popup.id}:${currentUid}`;
    if (officialViewedRef.current.has(viewKey)) return undefined;

    const delayMs = Math.max(0, Number(popup.popupDelaySeconds ?? 4)) * 1000;
    officialPopupTimerRef.current = setTimeout(async () => {
      try {
        const viewRef = doc(db, 'officialUpdates', popup.id, 'views', currentUid);
        const existing = await getDoc(viewRef);
        if (existing.exists()) {
          officialViewedRef.current.add(viewKey);
          return;
        }
      } catch (e) {}

      setOfficialPopupPost(popup);
      setShowOfficialPopup(true);
      markOfficialPostViewed(popup);

      const displayMs = durationToMsSafe(
        popup.popupDurationValue ?? popup.durationValue ?? 1,
        popup.popupDurationUnit ?? popup.durationUnit ?? 'seconds'
      );

      officialPopupHideTimerRef.current = setTimeout(() => {
        setShowOfficialPopup(false);
        setTimeout(() => setOfficialPopupPost(null), 320);
      }, displayMs);
    }, delayMs);

    return () => {
      if (officialPopupTimerRef.current) clearTimeout(officialPopupTimerRef.current);
      if (officialPopupHideTimerRef.current) clearTimeout(officialPopupHideTimerRef.current);
    };
  }, [
    currentUid,
    officialPosts.find(post => post.placement === 'popup')?.id,
    officialPosts.find(post => post.placement === 'popup')?.popupDelaySeconds,
    officialPosts.find(post => post.placement === 'popup')?.popupDurationValue,
    officialPosts.find(post => post.placement === 'popup')?.popupDurationUnit,
  ]);

  useEffect(() => {
    const timer = setInterval(() => {
      setOfficialPosts(prev => {
        const next = prev.filter(post => {
          if (post.placement === 'popup') return true;
          const expiresAt = toMillisSafe(post.expiresAt);
          return !expiresAt || expiresAt > Date.now();
        });
        saveOfficialPostsCache(next);
        return next;
      });
    }, 15000);
    return () => clearInterval(timer);
  }, []);

  // ==========================================
  // Selection & Pre-fetch Navigation Logic
  // ==========================================
  const handleItemClick = (item) => {
    if (selectedItems.length > 0) {
      if (selectedItems.find(i => i.id === item.id)) {
        setSelectedItems(selectedItems.filter(i => i.id !== item.id));
      } else {
        setSelectedItems([...selectedItems, item]);
      }
      return;
    }

    const itemId = item.id;
    const nextCount = (clickCountRef.current[itemId] || 0) + 1;
    clickCountRef.current[itemId] = nextCount;

    if (clickTimerRef.current[itemId]) {
      clearTimeout(clickTimerRef.current[itemId]);
    }

    clickTimerRef.current[itemId] = setTimeout(() => {
      if (clickCountRef.current[itemId] === 1) {
        if (item.isCommunity) {
          let preloadedCommunityCache = communityPreloadMemoryRef.current.get(item.id) || null;
          if (!preloadedCommunityCache) {
            try {
              const raw = localStorage.getItem(`nexora_comm_msgs_${item.id}`);
              if (raw) {
                const parsed = JSON.parse(raw);
                preloadedCommunityCache = Array.isArray(parsed)
                  ? { messages: parsed, unreadIds: [], firstUnreadId: null, unreadCount: Number(item.unreadCount?.[currentUid] || 0) }
                  : parsed;
              }
            } catch (e) {}
          }

          handleNavigate(`/community/${item.id}`, {
            state: {
              communityId: item.id,
              preloadedCommunityCache,
              preloadedCommunityData: item
            }
          });
        } else if (item.isGroup) {
          let preloadedGroupCache = groupPreloadMemoryRef.current.get(item.id) || null;
          if (!preloadedGroupCache) {
            try {
              const raw = localStorage.getItem(`nexora_group_msgs_${item.id}`);
              if (raw) {
                const parsed = JSON.parse(raw);
                preloadedGroupCache = Array.isArray(parsed)
                  ? { messages: parsed, unreadIds: [], unreadCount: Number(item.unreadCount?.[currentUid] || 0) }
                  : parsed;
              }
            } catch (e) {}
          }

          handleNavigate(`/group/${item.id}`, {
            state: {
              groupId: item.id,
              preloadedGroupCache,
              preloadedGroupData: item
            }
          });
        } else if (item.otherUid) {
          // Pre-fetch cache & pass targetUserData to prevent multi-step loading flicker
          const rId = [currentUid, item.otherUid].sort().join('_');
          const cachedChat = chatPreloadMemoryRef.current.get(rId) || getChatCache(rId);

          handleNavigate(`/chat/${item.otherUid}`, { 
            state: { 
              targetUserData: {
                fullName: item.title,
                photoURL: item.photoURL,
                phoneNumber: item.phoneNumber || item.otherPhoneNumber,
                isOnline: item.isOnline || false,
                role: item.role || 'user'
              },
              preloadedMessages: cachedChat.messages,
              preloadedChatCache: cachedChat
            } 
          });
        }
      }
      clickCountRef.current[itemId] = 0;
    }, 200);

    if (nextCount === 3) {
      clearTimeout(clickTimerRef.current[itemId]);
      clickCountRef.current[itemId] = 0;
      if (selectedItems.find(i => i.id === item.id)) {
        setSelectedItems(selectedItems.filter(i => i.id !== item.id));
      } else {
        setSelectedItems([...selectedItems, item]);
      }
    }
  };

  const handleAddCustomTab = async () => {
    const tabName = await window.nexoraPrompt("নতুন লিস্টের নাম দিন:");
    if (tabName && tabName.trim() !== '') {
      const newTabs = [...customTabs, tabName.trim()];
      setCustomTabs(newTabs);
      localStorage.setItem(`nexora_custom_tabs_${currentUid}`, JSON.stringify(newTabs));
    }
  };

  const handleTabTouchStart = (tab) => {
    if (!['All', 'Unread', 'Favourites', 'Group', 'Channel'].includes(tab)) {
      tabLongPressTimerRef.current = setTimeout(async () => {
        if (navigator.vibrate) navigator.vibrate(50);
        if (await window.nexoraConfirm(`আপনি কি "${tab}" লিস্টটি ডিলিট করতে চান?`)) {
          const newTabs = customTabs.filter(t => t !== tab);
          const newMap = { ...customListsMap };
          delete newMap[tab];
          setCustomTabs(newTabs);
          setCustomListsMap(newMap);
          localStorage.setItem(`nexora_custom_tabs_${currentUid}`, JSON.stringify(newTabs));
          localStorage.setItem(`nexora_custom_lists_map_${currentUid}`, JSON.stringify(newMap));
          if (activeTab === tab) setActiveTab('All');
        }
      }, 600);
    }
  };
  const handleTabTouchEnd = () => { if (tabLongPressTimerRef.current) clearTimeout(tabLongPressTimerRef.current); };

  const handleAddToList = (tabName) => {
    const newMap = { ...customListsMap };
    if (!newMap[tabName]) newMap[tabName] = [];
    selectedItems.forEach(item => { if (!newMap[tabName].includes(item.id)) newMap[tabName].push(item.id); });
    setCustomListsMap(newMap);
    localStorage.setItem(`nexora_custom_lists_map_${currentUid}`, JSON.stringify(newMap));
    setShowListSelectorModal(false); setSelectedItems([]);
  };

  const handleRemoveFromCustomList = async () => {
    if (await window.nexoraConfirm("সিলেক্ট করা চ্যাটগুলো এই লিস্ট থেকে রিমুভ করতে চান?")) {
      const newMap = { ...customListsMap };
      if (newMap[activeTab]) {
        const toRemove = selectedItems.map(i => i.id);
        newMap[activeTab] = newMap[activeTab].filter(id => !toRemove.includes(id));
        setCustomListsMap(newMap);
        localStorage.setItem(`nexora_custom_lists_map_${currentUid}`, JSON.stringify(newMap));
      }
      setSelectedItems([]);
    }
  };

  const handlePinAction = () => {
    let updated = [...pinnedIds];
    selectedItems.forEach(item => {
      if (updated.includes(item.id)) updated = updated.filter(pId => pId !== item.id);
      else if (updated.length < 4) updated.push(item.id);
    });
    setPinnedIds(updated); localStorage.setItem(`nexora_pins_${currentUid}`, JSON.stringify(updated)); setSelectedItems([]);
  };

  const handleFavouriteAction = () => {
    let updated = [...favouriteIds];
    selectedItems.forEach(item => {
      if (updated.includes(item.id)) updated = updated.filter(fId => fId !== item.id);
      else updated.push(item.id);
    });
    setFavouriteIds(updated); localStorage.setItem(`nexora_favs_${currentUid}`, JSON.stringify(updated)); setSelectedItems([]);
  };

  const handleDeleteAction = async () => {
    if (!currentUid || selectedItems.length === 0) return;

    if (await window.nexoraConfirm("আপনি কি নির্বাচিতগুলো শুধু আপনার Dashboard থেকে Remove করতে চান?")) {
      const now = Date.now();

      selectedItems.forEach(item => {
        dismissedDashboardRef.current[item.id] = now;
      });

      const selectedIds = new Set(selectedItems.map(item => item.id));
      setMessagesFeed(prev => prev.filter(item => !selectedIds.has(item.id)));
      setGroups(prev => prev.filter(item => !selectedIds.has(item.id)));
      setCommunities(prev => prev.filter(item => !selectedIds.has(item.id)));

      try {
        setMessagesFeed(prev => { localStorage.setItem('nexora_dash_chats', JSON.stringify(prev)); return prev; });
        setGroups(prev => { localStorage.setItem('nexora_dash_groups', JSON.stringify(prev)); return prev; });
        setCommunities(prev => { localStorage.setItem('nexora_dash_comms', JSON.stringify(prev)); return prev; });
      } catch (e) {}

      setSelectedItems([]);
    }
  };

  const pushBulkMessage = async (type, payloadExtra, summary) => {
    setIsUploading(true);
    try {
      for (let item of selectedItems) {
        const path = item.isGroup ? 'groups' : item.isComm ? 'communities' : 'chats';
        const msgData = {
          senderId: currentUid,
          senderName: currentUserData?.fullName || 'User',
          senderPhoto: currentUserData?.photoURL || '',
          type,
          createdAt: serverTimestamp(),
          timeStr: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          ...payloadExtra
        };
        await addDoc(collection(db, path, item.id, 'messages'), msgData);
        
        await updateDoc(doc(db, path, item.id), {
          lastMessage: `${currentUserData?.fullName || 'User'}: ${summary}`,
          updatedAt: serverTimestamp()
        });
      }
    } catch (e) {}
    setIsUploading(false); setShowBulkSmsModal(false); setSelectedItems([]); setBulkText(''); setUploadProgress(0);
  };

  const handleBulkTextSubmit = (e) => {
    e.preventDefault();
    if (!bulkText.trim()) return;
    pushBulkMessage('text', { text: bulkText }, bulkText);
  };

  const handleBulkMediaSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadProgress(25);
    if (file.type.startsWith('video/')) {
      const reader = new FileReader();
      reader.onprogress = (ev) => { if (ev.lengthComputable) setUploadProgress(Math.round((ev.loaded / ev.total) * 80)); };
      reader.readAsDataURL(file);
      reader.onload = async () => {
        setUploadProgress(90); await pushBulkMessage('video', { mediaData: reader.result }, '🎥 Video');
      };
    } else {
      const base64Img = await compressImage(file);
      setUploadProgress(85); await pushBulkMessage('image', { mediaData: base64Img }, '📷 Photo');
    }
  };

  const handleBulkFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadProgress(30);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async () => {
      setUploadProgress(90); await pushBulkMessage('file', { fileData: reader.result, fileName: file.name, fileSize: (file.size / (1024 * 1024)).toFixed(1) + ' MB' }, `📄 ${file.name}`);
    };
  };

  const startMicPreviewStream = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioCtx.createAnalyser();
      const source = audioCtx.createMediaStreamSource(stream);
      analyser.fftSize = 32; source.connect(analyser);
      audioContextRef.current = audioCtx; analyserRef.current = analyser;
      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const updateWave = () => {
        analyser.getByteFrequencyData(dataArray);
        const bars = Array.from(dataArray.slice(0, 10)).map(v => Math.max(10, v * 0.45));
        setWaveBars(bars);
        animFrameRef.current = requestAnimationFrame(updateWave);
      };
      updateWave(); return stream;
    } catch (e) { 
      setShowVoiceModal(false); return null; 
    }
  };

  const stopMicPreviewStream = () => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') audioContextRef.current.close().catch(() => {});
  };

  const openVoiceModal = async () => { setShowVoiceModal(true); setIsRecording(false); setRecordingTime(0); await startMicPreviewStream(); };

  const handleCircleVoiceClick = async () => {
    if (isRecording) { stopAndSendVoice(); } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaRecorderRef.current = new MediaRecorder(stream);
        audioChunksRef.current = [];
        mediaRecorderRef.current.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
        mediaRecorderRef.current.onstop = () => {
          stopMicPreviewStream();
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          const reader = new FileReader(); reader.readAsDataURL(audioBlob);
          reader.onloadend = () => { pushBulkMessage('voice', { audioData: reader.result, duration: `${recordingTime}s` }, '🎙️ Voice message'); };
          stream.getTracks().forEach((t) => t.stop());
        };
        mediaRecorderRef.current.start(); setIsRecording(true); setRecordingTime(0);
        recordingIntervalRef.current = setInterval(() => { setRecordingTime((prev) => prev + 1); }, 1000);
      } catch (err) {}
    }
  };

  const stopAndSendVoice = () => {
    if (mediaRecorderRef.current && isRecording) { mediaRecorderRef.current.stop(); setIsRecording(false); clearInterval(recordingIntervalRef.current); setShowVoiceModal(false); }
  };
  const cancelVoiceModal = () => {
    if (mediaRecorderRef.current && isRecording) { mediaRecorderRef.current.onstop = null; mediaRecorderRef.current.stop(); }
    stopMicPreviewStream(); clearInterval(recordingIntervalRef.current); setIsRecording(false); setShowVoiceModal(false);
  };

  const syncPhoneContacts = async () => {
    if ('contacts' in navigator && 'select' in window.ContactsManager.prototype) {
      try {
        const selected = await navigator.contacts.select(['name', 'tel'], { multiple: true });
        if (selected && selected.length > 0) {
          selected.forEach(c => {
            const rawPhone = c.tel && c.tel[0] ? c.tel[0] : '';
            const cName = c.name && c.name[0] ? c.name[0] : '';
            if (rawPhone && cName) { const formatted = normalizePhone(rawPhone); saveLocalContact(formatted, cName); }
          });
          setLocalContacts(getLocalContacts()); 
        }
      } catch (err) {}
    }
  };

  const handleManualAddContact = async () => {
    const formatted = normalizePhone(newManualPhone);
    const contactName = newManualName.trim();
    if (!formatted || !contactName || !currentUid) return;

    try {
      // Same verification logic as Contract.js: the number must belong to
      // an existing Nexora user before it can be added.
      const userSnap = await getDoc(doc(db, 'users', formatted));
      if (!userSnap.exists()) {
        window.nexoraAlert('এই নাম্বারে কোনো Nexora ইউজার পাওয়া যায়নি।');
        return;
      }

      const userData = userSnap.data() || {};
      const contactUid = userData.uid || formatted;
      const ref = doc(db, 'connections', currentUid);
      const snap = await getDoc(ref);
      const existing = snap.exists() ? (snap.data()?.contacts || {}) : {};

      const updatedContacts = {
        ...existing,
        [formatted]: {
          name: contactName,
          phoneNumber: formatted,
          uid: contactUid
        }
      };

      // Preserve Contract.js behaviour: update an existing connections doc;
      // create one if it does not exist yet.
      if (snap.exists()) {
        await updateDoc(ref, { contacts: updatedContacts });
      } else {
        await setDoc(ref, { contacts: updatedContacts }, { merge: true });
      }

      saveLocalContact(formatted, contactName);
      setLocalContacts(getLocalContacts());
      setConnectionContacts(updatedContacts);

      // Keep the same profile in Dashboard cache/IndexedDB so it does not
      // need to be fetched again merely to display the contact.
      const profile = { id: userSnap.id, ...userData, phoneNumber: formatted, uid: contactUid };
      setUserProfilesMap((prev) => {
        const next = { ...prev, [contactUid]: profile, [formatted]: profile };
        try {
          localStorage.setItem(`nexora_dash_users_map_${currentUid}`, JSON.stringify(next));
          localStorage.setItem('nexora_dash_users_map', JSON.stringify(next));
        } catch (e) {}
        idbPut('profiles', contactUid, profile);
        return next;
      });

      setNewManualPhone('');
      setNewManualName('');
    } catch (err) {
      console.error('Failed to save connection contact:', err);
      window.nexoraAlert('Contact save করা সম্ভব হয়নি। আবার চেষ্টা করুন।');
    }
  };

  // Contract/profile sync: only read users that are actually in the saved
  // connection list. This is intentionally lazy so Dashboard refresh never
  // scans the entire users collection.
  useEffect(() => {
    if (!showContactModal || !currentUid) return;
    const phones = Object.keys({ ...localContacts, ...connectionContacts })
      .map(normalizePhone)
      .filter(Boolean);
    if (!phones.length) return;

    const uniquePhones = [...new Set(phones)];
    const loadedPhones = new Set(getCachedData(`nexora_contract_profile_phones_${currentUid}`));
    const missingPhones = uniquePhones.filter(phone => !loadedPhones.has(phone));
    if (!missingPhones.length) return;

    const chunks = [];
    for (let i = 0; i < missingPhones.length; i += 30) chunks.push(missingPhones.slice(i, i + 30));
    let cancelled = false;

    (async () => {
      const found = [];
      for (const chunk of chunks) {
        try {
          const snap = await getDocs(query(collection(db, 'users'), where('phoneNumber', 'in', chunk)));
          snap.forEach(d => found.push({ id: d.id, ...d.data() }));
        } catch (e) {}
      }
      if (cancelled || !found.length) return;

      const nextMap = { ...getCachedObj(`nexora_dash_users_map_${currentUid}`) };
      found.forEach(u => {
        if (u.uid) nextMap[u.uid] = u;
        if (u.phoneNumber) nextMap[normalizePhone(u.phoneNumber)] = u;
        if (u.id) nextMap[u.id] = u;
      });
      setUserProfilesMap(nextMap);
      setAllUsers(found.filter(u => u.uid !== currentUid));
      try {
        localStorage.setItem(`nexora_dash_users_map_${currentUid}`, JSON.stringify(nextMap));
        localStorage.setItem(`nexora_dash_users_${currentUid}`, JSON.stringify(found));
        localStorage.setItem('nexora_dash_users_map', JSON.stringify(nextMap));
        localStorage.setItem('nexora_dash_users', JSON.stringify(found));
      } catch (e) {}
      found.forEach(u => { if (u.uid) idbPut('profiles', u.uid, u); });
      try {
        const mergedLoaded = [...new Set([...loadedPhones, ...missingPhones])];
        localStorage.setItem(`nexora_contract_profile_phones_${currentUid}`, JSON.stringify(mergedLoaded));
      } catch (e) {}
    })();

    return () => { cancelled = true; };
  }, [showContactModal, currentUid, localContacts, connectionContacts]);

  const combinedList = [...recentChats, ...communities, ...groups];

  const processedList = combinedList.map(item => {
    const getSortTime = (value) => {
      if (!value) return 0;
      if (typeof value === 'number') return value;
      if (value.seconds) return value.seconds * 1000;
      if (typeof value.toMillis === 'function') return value.toMillis();
      return 0;
    };

    if (item.isCommunity) {
      return { ...item, title: item.name || 'Nexora Community', subText: item.lastMessage || 'Tap to view community...', isComm: true, isGroup: false, sortTime: getSortTime(item.updatedAt) };
    } else if (item.isGroup) {
      return { ...item, title: item.name || item.groupName || 'Nexora Group', subText: item.lastMessage || 'Tap to view group...', isComm: false, isGroup: true, sortTime: getSortTime(item.updatedAt) };
    } else {
      const isSelf = item.otherUid === currentUid;
      const targetUser = isSelf ? currentUserData : userProfilesMap[item.otherUid];
      const normPhone = normalizePhone(targetUser?.phoneNumber || item.otherPhoneNumber || item.phoneNumber);
      const savedName = localContacts[normPhone];
      
      const isAdminUser = targetUser?.role?.toLowerCase() === 'admin';
      const isPremiumUser = targetUser?.role?.toLowerCase() === 'premium';
      
      const titleName = (isAdminUser || isPremiumUser) ? (targetUser?.fullName || 'Nexora User') : (isSelf ? 'You (Note to self)' : (savedName || normPhone || targetUser?.fullName || 'Unknown User'));
      const userPhoto = targetUser?.photoURL || item.photoURL;

      return {
        ...item, title: titleName, photoURL: userPhoto, subText: item.lastMessage || 'No messages yet',
        isComm: false, isGroup: false, isOnline: targetUser?.isOnline || false,
        isAdminUser: isAdminUser, isPremiumUser: isPremiumUser,
        adminBio: targetUser?.bio || 'সম্মানিত Nexora ইউজার, আমাদের সাথে কানেক্ট থাকার জন্য অসংখ্য ধন্যবাদ!',
        sortTime: getSortTime(item.updatedAt)
      };
    }
  });

  const filteredList = processedList.filter(item => {
    const queryLower = searchQuery.toLowerCase();
    const matchesSearch = item.title.toLowerCase().includes(queryLower) || item.subText.toLowerCase().includes(queryLower);
    if (!matchesSearch) return false;

    if (activeTab === 'Unread') return item.unreadCount && item.unreadCount[currentUid] > 0;
    if (activeTab === 'Favourites') return favouriteIds.includes(item.id);
    if (activeTab === 'Group' || activeTab === 'Groups') return item.isGroup || item.isComm;
    if (customTabs.includes(activeTab)) return customListsMap[activeTab]?.includes(item.id);

    return true;
  });

  const pinnedList = filteredList.filter(item => pinnedIds.includes(item.id));
  const unpinnedList = filteredList.filter(item => !pinnedIds.includes(item.id));
  unpinnedList.sort((a, b) => b.sortTime - a.sortTime);
  const finalDisplayList = [...pinnedList, ...unpinnedList];

  const mergedConnectionContacts = { ...localContacts };
  Object.entries(connectionContacts || {}).forEach(([phone, value]) => {
    const name = typeof value === 'string' ? value : value?.name;
    if (phone && name) mergedConnectionContacts[phone] = name;
  });

  const phonebookKeys = Object.keys(mergedConnectionContacts);
  const registeredPhones = allUsers.map(u => normalizePhone(u.phoneNumber)).filter(Boolean);
  if (currentUserData) registeredPhones.push(normalizePhone(currentUserData.phoneNumber));

  const nexoraSavedContacts = phonebookKeys.map((phone) => {
    const matchedUser = allUsers.find(u => normalizePhone(u.phoneNumber) === phone);
    if (matchedUser) return matchedUser;
    if (currentUserData && normalizePhone(currentUserData.phoneNumber) === phone) return { ...currentUserData, isSelf: true };
    return { uid: `contact_${phone}`, phoneNumber: phone, fullName: mergedConnectionContacts[phone], photoURL: '', isSavedConnection: true };
  });

  const inviteContactsList = phonebookKeys
    .filter(phoneKey => !registeredPhones.includes(phoneKey))
    .map(phoneKey => ({ phone: phoneKey, name: mergedConnectionContacts[phoneKey] }));

  return (
    <div style={styles.container}>
      <style>{`
        body, html, #root { margin: 0; padding: 0; height: 100dvh; width: 100vw; overflow: hidden; background: #fafafa; }
        * { box-sizing: border-box; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
        
        .dash-shell { width: 100%; max-width: 480px; height: calc(100dvh - 64px); background: #ffffff; display: flex; flex-direction: column; position: relative; overflow: hidden; box-shadow: 0 0 40px rgba(0,0,0,0.04); will-change: transform; }
        @media (min-width: 768px) { .dash-shell { height: 92vh; border-radius: 36px; border: 1px solid #f1f5f9; } }

        .fixed-top-header { padding: 16px 20px 8px; background: #ffffff; z-index: 20; flex-shrink: 0; }
        .dashboard-story-rail { display: flex; align-items: flex-start; gap: 14px; overflow-x: auto; padding: 12px 20px 10px; background: #ffffff; flex-shrink: 0; scrollbar-width: none; }
        .dashboard-story-rail::-webkit-scrollbar { display: none; }
        .dashboard-story-item { position: relative; width: 70px; min-width: 70px; display: flex; flex-direction: column; align-items: center; gap: 6px; border: 0; background: transparent; color: #475569; font-size: 11px; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; cursor: pointer; }
        .dashboard-story-button { display: flex; flex-direction: column; align-items: center; gap: 6px; width: 70px; min-width: 70px; padding: 0; border: 0; background: transparent; color: inherit; font: inherit; }
        .dashboard-story-avatar { box-sizing: border-box; width: 58px; height: 58px; flex: 0 0 58px; aspect-ratio: 1; display: grid; place-items: center; padding: 3px; border-radius: 50%; overflow: hidden; border: 2px solid #cbd5e1; background: transparent; }
        .dashboard-story-avatar.unseen { border-color: #22a65a; }
        .dashboard-story-avatar.seen { border-color: #cbd5e1; }
        .dashboard-story-avatar img { display: block; width: 100%; height: 100%; aspect-ratio: 1; border-radius: 50%; object-fit: cover; }
        .dashboard-story-compose { position: absolute; right: 12px; bottom: 20px; display: grid; place-items: center; width: 22px; height: 22px; border: 2px solid #fff; border-radius: 50%; background: #22a65a; color: #fff; cursor: pointer; }
        .selection-top-bar { padding: 14px 20px; background: #1e293b; color: white; display: flex; align-items: center; justify-content: space-between; z-index: 50; flex-shrink: 0; animation: slideDown 0.2s ease-out; }
        @keyframes slideDown { from { transform: translateY(-100%); } to { transform: translateY(0); } }

        .search-box-pill { background: #f1f5f9; border-radius: 24px; padding: 10px 16px; display: flex; align-items: center; gap: 10px; margin-top: 14px; }
        .filter-tabs-bar { display: flex; gap: 8px; overflow-x: auto; padding: 12px 20px 8px; background: #ffffff; border-bottom: 1px solid #f8fafc; flex-shrink: 0; scrollbar-width: none; }
        .filter-tabs-bar::-webkit-scrollbar { display: none; }

        .tab-pill { padding: 6px 18px; border-radius: 20px; font-size: 13px; font-weight: 600; border: none; background: #f1f5f9; color: #475569; cursor: pointer; white-space: nowrap; transition: all 0.2s ease; }
        .tab-pill.active { background: #62b875; color: #ffffff; box-shadow: 0 4px 12px rgba(98, 184, 117, 0.25); }

        .scrollable-feed-area { flex: 1; overflow-y: auto; padding: 8px 0; -webkit-overflow-scrolling: touch; }
        .feed-row { padding: 12px 20px; display: flex; align-items: center; justify-content: space-between; cursor: pointer; transition: background 0.15s ease; user-select: none; }
        .feed-row.selected { background: #e2e8f0; }
        .feed-row:active { background: #f8fafc; }

        .unread-badge { background: #62b875; color: white; font-size: 11px; font-weight: 800; min-width: 20px; height: 20px; padding: 0 6px; border-radius: 10px; display: flex; align-items: center; justify-content: center; }
        .comm-tag { font-size: 11px; font-weight: 700; padding: 3px 10px; border-radius: 12px; }

        .list-selector-card { display: flex; align-items: center; gap: 12px; width: 100%; padding: 14px; border: 1px solid #e5f3e8; border-radius: 16px; background: linear-gradient(135deg, #f8fffa, #ffffff); color: #166534; cursor: pointer; text-align: left; box-shadow: 0 8px 18px rgba(34,197,94,.08); transition: transform .18s ease, box-shadow .18s ease; }
        .list-selector-card:active { transform: scale(.98); box-shadow: 0 4px 10px rgba(34,197,94,.1); }
        .list-selector-icon { width: 38px; height: 38px; flex: 0 0 38px; display: flex; align-items: center; justify-content: center; border-radius: 12px; background: #dcfce7; color: #15803d; font-size: 20px; }
        .list-selector-name { font-size: 14px; font-weight: 900; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

        .fixed-bottom-nav { height: 60px; padding-bottom: max(4px, env(safe-area-inset-bottom)); background: #ffffff; border-top: 1px solid #f1f5f9; display: flex; align-items: center; justify-content: space-around; z-index: 30; flex-shrink: 0; }
        .nav-item-btn { display: flex; flex-direction: column; align-items: center; justify-content: center; background: none; border: none; color: #94a3b8; font-size: 11px; font-weight: 600; gap: 3px; cursor: pointer; flex: 1; }
        .nav-item-btn.active { color: #62b875; font-weight: 700; }

        .full-contact-sheet { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: #ffffff; z-index: 200; display: flex; flex-direction: column; animation: slideInRight 0.25s ease-out; }
        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .action-row-item { display: flex; align-items: center; gap: 16px; padding: 14px 20px; cursor: pointer; }
        .action-icon-circle { width: 44px; height: 44px; border-radius: 50%; background: #62b875; color: white; display: flex; align-items: center; justify-content: center; }

        .modal-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.6); backdrop-filter: blur(4px); z-index: 300; display: flex; align-items: flex-end; justify-content: center; padding: 20px;}
        .card-modal { width: 100%; max-width: 480px; background: white; border-radius: 28px; padding: 24px; box-shadow: 0 20px 40px rgba(0,0,0,0.15); max-height: 85vh; overflow-y: auto; margin-bottom: 0; animation: slideUp 0.3s ease-out; }
        
        .voice-card-bottom { width: 100%; max-width: 480px; background: #ffffff; border-radius: 28px 28px 0 0; padding: 20px 24px 28px; box-shadow: 0 -10px 30px rgba(0,0,0,0.12); position: absolute; bottom: 0; text-align: center; border-top: 1px solid #f1f5f9; animation: slideUp 0.25s ease-out; z-index: 400;}
        .voice-circle { width: 96px; height: 96px; border-radius: 50%; background: #ffffff; border: 2px solid #62b875; display: flex; align-items: center; justify-content: center; margin: 12px auto 0; cursor: pointer; transition: all 0.2s; box-shadow: 0 8px 20px rgba(98, 184, 117, 0.1); }
        .voice-circle.recording { border-color: #ef4444; box-shadow: 0 8px 20px rgba(239, 68, 68, 0.2); animation: pulseRed 1.5s infinite; }
        @keyframes pulseRed { 0% { transform: scale(0.96); } 50% { transform: scale(1.04); } 100% { transform: scale(0.96); } }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }

        .chat-input-form { padding: 10px 12px; background: #ffffff; display: flex; align-items: center; gap: 8px; z-index: 20; border-top: 1px solid #f1f5f9; flex: 0 0 auto; border-radius: 16px;}
        .black-action-btn { width: 48px; height: 48px; border-radius: 50%; background: rgba(24, 24, 27, 0.8); color: white; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; }

        .special-admin-wrapper { margin: 6px 16px; perspective: 1500px; cursor: pointer; user-select: none; }
        .special-admin-wrapper.selected { border-radius: 20px; background: #e2e8f0; }
        
        .flip-inner { position: relative; width: 100%; transform-style: preserve-3d; animation: mochorDoubleFlip 24s infinite; }
        .flip-face { width: 100%; backface-visibility: hidden; -webkit-backface-visibility: hidden; border-radius: 18px; }
        .flip-front { position: relative; z-index: 2; }
        .flip-back { position: absolute; top: 0; left: 0; height: 100%; transform: rotate3d(-1, 1, 0, 180deg); z-index: 1; }

        @keyframes mochorDoubleFlip {
            0%, 42% { transform: rotate3d(-1, 1, 0, 0deg); }
            45% { transform: rotate3d(-1, 1, 0, 45deg) scale(0.95); } 
            50%, 92% { transform: rotate3d(-1, 1, 0, 180deg); } 
            95% { transform: rotate3d(-1, 1, 0, 225deg) scale(0.95); } 
            100% { transform: rotate3d(-1, 1, 0, 360deg); }
        }

        .card-border-box { position: relative; border-radius: 18px; padding: 1.5px; background: rgba(255, 255, 255, 0.1); box-shadow: 0 12px 35px rgba(88, 185, 112, 0.45), 0 0 15px rgba(88, 185, 112, 0.3); overflow: hidden; width: 100%; height: 100%; }
        .card-border-box::before, .card-border-box::after { content: ''; position: absolute; top: -50%; left: -50%; width: 200%; height: 200%; background: conic-gradient(transparent, transparent, transparent, #8eed9c, transparent 30%); z-index: 1; }
        .card-border-box::before { animation: rotateLight 4s linear infinite; }
        .card-border-box::after { background: conic-gradient(transparent, #a8f0b2, transparent 25%, transparent, transparent); animation: rotateLightReverse 5s ease-in-out infinite reverse; }
        
        @keyframes rotateLight { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes rotateLightReverse { 0% { transform: rotate(360deg); } 100% { transform: rotate(0deg); } }

        .glass-card-inner { position: relative; z-index: 2; background: rgba(245, 252, 247, 0.85); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); border: 1px solid rgba(255, 255, 255, 0.9); border-radius: 16px; padding: 12px 15px; display: flex; align-items: center; gap: 15px; }

        .avatar-wrapper { position: relative; flex-shrink: 0; perspective: 800px; width: 50px; height: 50px; }
        .anim-coin-flip { animation: coinFlip 4s ease-in-out infinite; transform-style: preserve-3d; }
        @keyframes coinFlip {
            0%, 75% { transform: rotateY(0deg) scale(1); opacity: 1; }
            85%, 90% { transform: rotateY(180deg) scale(0.5); opacity: 0; }
            100% { transform: rotateY(360deg) scale(1); opacity: 1; }
        }

        .dynamic-shield { width: 18px; height: 18px; flex-shrink: 0; }
        .shield-color { animation: colorCycle 12s ease-in-out infinite alternate; }
        @keyframes colorCycle { 0%, 10% { fill: #22c55e; } 25%, 35% { fill: #eab308; } 50%, 60% { fill: #f97316; } 75%, 85% { fill: #ef4444; } 100% { fill: #3b82f6; } }

        .status-marquee { position: relative; height: 18px; margin-top: 5px; width: 100%; overflow: hidden; white-space: nowrap; -webkit-mask-image: linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%); mask-image: linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%); }
        .animated-text { display: inline-block; padding-left: 100%; font-size: 11px; color: #333; font-weight: 500; animation: textMarquee 12s linear infinite; }
        @keyframes textMarquee { 0% { transform: translateX(0); } 100% { transform: translateX(-100%); } }

        .blinking-admin-online { position: absolute; top: 12px; right: 15px; font-size: 11px; font-weight: 800; color: #22c55e; animation: blink 1.2s ease-in-out infinite; }
        .blinking-admin-offline { position: absolute; top: 12px; right: 15px; font-size: 11px; font-weight: 800; color: #ef4444; animation: blink 1.2s ease-in-out infinite; }
        @keyframes blink { 50% { opacity: 0.3; } }

        .official-popup-layer { position: fixed; top: max(12px, env(safe-area-inset-top)); left: 50%; width: min(92vw, 480px); z-index: 1000; transform: translate(-50%, -125%); opacity: 0; pointer-events: none; transition: transform .34s cubic-bezier(.22,1,.36,1), opacity .26s ease; filter: drop-shadow(0 18px 35px rgba(0,0,0,.18)); }
        .official-popup-layer.visible { transform: translate(-50%, 0); opacity: 1; }
        .official-popup-layer.leaving { transform: translate(-50%, -125%); opacity: 0; }
        .official-post-card { width: 100%; min-height: 150px; border-radius: 22px; background: linear-gradient(135deg, #f7fff9 0%, #ffffff 72%); border: 1px solid rgba(98,184,117,.24); box-shadow: 0 14px 40px rgba(34,197,94,.12); overflow: hidden; }
        .official-post-card.banner-card { margin: 8px 12px 4px; width: calc(100% - 24px); }
        .official-post-media { width: 100%; display: block; object-fit: cover; }
        .official-media-stage { position: relative; width: 100%; aspect-ratio: 3220 / 1120; overflow: hidden; cursor: pointer; background: transparent; }
        .official-media-stage .official-post-media { width: 100%; height: 100%; object-fit: cover; object-position: center; }
        .official-post-video { background: transparent; }
        .official-post-image { max-height: none; }
        .official-media-top { position: absolute; top: 0; left: 0; right: 0; z-index: 2; display: flex; align-items: center; gap: 10px; padding: 12px 14px; background: transparent; color: #fff; transition: opacity .24s ease, transform .24s ease; }
        .official-media-top.hidden { opacity: 0; transform: translateY(-12px); pointer-events: none; }
        .official-media-top .official-post-name { color: #fff; text-shadow: 0 1px 3px rgba(0,0,0,.4); }
        .official-media-top .official-post-subs { color: rgba(255,255,255,.86); }
        .media-post-body { padding-top: 5px; }
        .official-post-body { padding: 11px 16px 2px;  }
        .official-post-head { display: flex; align-items: center; gap: 10px; }
        .official-post-logo { width: 46px; height: 46px; border-radius: 50%; object-fit: cover; border: 2px solid rgba(98,184,117,.25); flex-shrink: 0; }
        .official-post-name { font-size: 15px; font-weight: 900; color: #0f172a; display: flex; align-items: center; gap: 6px; }
        .official-verified { width: 17px; height: 17px; border-radius: 50%; background: #22c55e; color: #fff; display: inline-flex; align-items: center; justify-content: center; font-size: 10px; }
        .official-post-subs { margin-top: 2px; font-size: 11px; color: #64748b; }
        .official-post-text { margin-top: 3px; color: #1e293b; font-size: 14px; line-height: 1.3; text-align: left; white-space: pre-wrap; word-break: break-word; display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; overflow: hidden; }
        .official-post-audio { width: 100%; margin-top: 13px; }
        .official-apk-box { margin-top: 12px; padding: 13px; border-radius: 15px; background: #f0fdf4; border: 1px solid #bbf7d0; }
        .official-apk-name { font-size: 15px; font-weight: 900; color: #166534; }
        .official-download-btn { display: inline-flex; align-items: center; justify-content: center; margin-top: 10px; padding: 10px 15px; border-radius: 12px; background: #62b875; color: #fff; text-decoration: none; font-weight: 800; font-size: 13px; }
        .official-post-banner-wrap { flex-shrink: 0; background: #fff; border-bottom: 1px solid #f1f5f9; }
        .official-post-card { user-select: none; -webkit-user-select: none; touch-action: manipulation; }
        .official-post-interactions { display: flex; align-items: center; justify-content: space-between; min-height: 22px; margin-top: 0; border-top: 1px solid #e2e8f0; padding-top: 0; margin-bottom: 0; }
        .official-reaction-wrap { position: relative; display: inline-flex; }
        .official-reaction-btn { border: 0; background: transparent; color: #64748b; cursor: pointer; font-weight: 800; font-size: 15px; padding: 1px 8px 1px 0; line-height: 1; }
        .official-reaction-btn.active { color: #e11d48; }
        .official-reaction-btn span { margin-left: 4px; color: #0f172a; font-size: 12px; vertical-align: middle; }
        .official-reaction-picker { position: absolute; z-index: 5; bottom: 38px; left: 0; display: flex; gap: 3px; padding: 7px; border: 1px solid #dbe5df; border-radius: 22px; background: #fff; box-shadow: 0 8px 24px rgba(15,23,42,.16); }
        .official-reaction-picker button { border: 0; background: transparent; cursor: pointer; font-size: 21px; padding: 2px; }
        .official-comment-open-btn { border: 0; background: transparent; color: #64748b; cursor: pointer; font-weight: 800; font-size: 12px; padding: 1px 0 1px 12px; line-height: 1; }
        .official-comment-open-btn span { margin-left: 4px; color: #0f172a; }
        .official-comments-modal { position: fixed; inset: 0; z-index: 1200; display: flex; align-items: flex-end; justify-content: center; padding: 16px; background: rgba(15,23,42,.42); }
        .official-comments-sheet { width: min(100%, 480px); max-height: min(78vh, 620px); display: flex; flex-direction: column; padding: 16px; border-radius: 20px 20px 12px 12px; background: #fff; box-shadow: 0 16px 45px rgba(15,23,42,.25); }
        .official-comments-header { display: flex; align-items: center; justify-content: space-between; color: #0f172a; font-size: 16px; }
        .official-comments-header button { border: 0; background: transparent; color: #64748b; cursor: pointer; font-size: 24px; }
        .official-comment-list { overflow-y: auto; }
        .official-comment-list-modal { min-height: 100px; margin-top: 12px; }
        .official-comment { display: flex; gap: 6px; padding: 5px 0; color: #334155; font-size: 12px; line-height: 1.35; word-break: break-word; }
        .official-comment strong { color: #0f172a; flex-shrink: 0; }
        .official-comment-form { display: flex; gap: 6px; margin-top: 7px; }
        .official-comment-form-top { order: 0; margin-top: 13px; }
        .official-no-comments { padding: 28px 0; text-align: center; color: #94a3b8; font-size: 13px; }
        .official-comment-form input { min-width: 0; flex: 1; border: 1px solid #dbe5df; border-radius: 10px; padding: 8px 10px; outline: none; color: #0f172a; background: #f8fbf9; }
        .official-comment-form button { border: 0; border-radius: 10px; padding: 0 11px; background: #62b875; color: white; font-weight: 800; cursor: pointer; }
        .official-comment-form button:disabled { opacity: .45; cursor: default; }
      `}</style>

      {/* Select Contact পেজ */}
      {showContactModal && (
        <div className="full-contact-sheet">
          <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '16px', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
            <button onClick={closeContactModal} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
            <div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>Select contact</h3>
              <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>
                {nexoraSavedContacts.length} contacts
              </span>
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            
            <button 
              onClick={syncPhoneContacts}
              style={{ width: '90%', margin: '14px auto 6px', padding: '12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '16px', fontWeight: '800', color: '#16a34a', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
            >
              <span>📱</span> <span>Sync Phonebook Contacts</span>
            </button>

            {/* ম্যানুয়ালি কন্টাক্ট সেভ সেকশন */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', background: '#fafafa', margin: '10px 0' }}>
              <div style={{ fontSize: '13px', fontWeight: '800', color: '#64748b', marginBottom: '10px' }}>Add New Contact Manually</div>
              <input 
                type="text" 
                placeholder="Contact Name" 
                value={newManualName}
                onChange={e => setNewManualName(e.target.value)}
                style={{ width: '100%', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '12px', marginBottom: '8px', fontSize: '14px', outline: 'none' }}
              />
              <div style={{ display: 'flex', gap: '8px' }}>
                <input 
                  type="tel" 
                  placeholder="Phone Number (e.g. 017...)" 
                  value={newManualPhone}
                  onChange={e => setNewManualPhone(e.target.value)}
                  style={{ flex: 1, padding: '12px', border: '1px solid #e2e8f0', borderRadius: '12px', fontSize: '14px', outline: 'none' }}
                />
                <button 
                  onClick={handleManualAddContact}
                  style={{ padding: '0 20px', background: '#62b875', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '800', fontSize: '14px', cursor: 'pointer' }}
                >
                  Save
                </button>
              </div>
            </div>

            <div className="action-row-item" onClick={() => {
              // Do not call history.back() and navigate() together. The modal
              // history entry can otherwise race with /group navigation.
              setShowContactModal(false);
              if (window.history.state?.modal === 'contact') {
                window.history.replaceState(null, '', window.location.href);
              }
              handleNavigate('/group');
            }}>
              <div className="action-icon-circle">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="17" y1="11" x2="23" y2="11"/></svg>
              </div>
              <span style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>New group</span>
            </div>

            <div className="action-row-item" onClick={() => { closeContactModal(); handleNavigate('/community'); }}>
              <div className="action-icon-circle">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/></svg>
              </div>
              <span style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>New community</span>
            </div>

            <div style={{ padding: '14px 20px 6px', fontSize: '13px', fontWeight: '800', color: '#64748b' }}>
              Contacts on Nexora
            </div>

            {nexoraSavedContacts.length > 0 ? (
              nexoraSavedContacts.map((u) => {
                const uPhoneNorm = normalizePhone(u.phoneNumber);
                const savedContactName = u.isSelf ? 'You (Message yourself)' : (localContacts[uPhoneNorm] || uPhoneNorm);
                
                const uRole = u.role?.toLowerCase();
                const isAppAdmin = uRole === 'admin';
                const isSpecialUser = isAppAdmin || uRole === 'premium';
                
                const displayTitle = isSpecialUser && !u.isSelf ? u.fullName : savedContactName;

                return (
                  <div 
                    key={u.uid || u.id}
                    onClick={() => {
                      if (!u.isSavedConnection) {
                        closeContactModal();
                        handleNavigate(`/chat/${u.uid || u.id}`, { state: { targetUserData: u } });
                      }
                    }}
                    style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: '14px', cursor: u.isSavedConnection ? 'default' : 'pointer', borderBottom: '1px solid #f8fafc' }}
                  >
                    <div className={isSpecialUser ? 'anim-coin-flip' : ''} style={{ width: '44px', height: '44px', perspective: '800px', flexShrink: 0 }}>
                      <img 
                        src={u.photoURL || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150"} 
                        alt="User" 
                        style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} 
                      />
                    </div>
                    
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                      <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: isAppAdmin ? '#15803d' : '#0f172a', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayTitle}</span>
                        {isSpecialUser && (
                          <svg className="dynamic-shield" viewBox="0 0 100 100" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
                            <path className="shield-color" d="M50 5 L90 20 L90 50 C90 75 50 95 50 95 C50 95 10 75 10 50 L10 20 Z"/>
                            <path d="M50 11 L84 24 L84 49 C84 70 50 87 50 87 C50 87 16 70 16 49 L16 24 Z" fill="none" stroke="white" strokeWidth="2.5" opacity="0.8"/>
                            <path d="M32 48 L45 61 L68 38" stroke="white" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </h4>
                      <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>
                        {u.isSelf ? 'Message yourself' : u.phoneNumber}
                      </p>
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ padding: '12px 20px', fontSize: '13px', color: '#94a3b8' }}>
                No saved contacts yet. Add a contact above to create your personal connection list.
              </div>
            )}

            {inviteContactsList.length > 0 && (
              <>
                <div style={{ padding: '18px 20px 6px', fontSize: '13px', fontWeight: '800', color: '#64748b' }}>
                  Invite to Nexora
                </div>
                {inviteContactsList.map((inv, idx) => (
                  <div 
                    key={idx}
                    style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f8fafc' }}
                  >
                    <div>
                      <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>{inv.name}</h4>
                      <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>{inv.phone}</p>
                    </div>
                    <button 
                      onClick={() => (`Invited ${inv.name} (${inv.phone}) to Nexora!`)}
                      style={{ padding: '6px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#16a34a', borderRadius: '20px', fontWeight: '800', fontSize: '12px', cursor: 'pointer' }}
                    >
                      Invite
                    </button>
                  </div>
                ))}
              </>
            )}

          </div>
        </div>
      )}

      {/* List Selector Modal */}
      {showListSelectorModal && (
          <div className="modal-overlay" style={{ alignItems: 'center' }} onClick={() => setShowListSelectorModal(false)}>
            <div className="card-modal" onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: '800' }}>Select List to Add</h3>
              {customTabs.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>No custom list available. Create one first using the + icon on tabs.</div>
              ) : (
                customTabs.map((tab, index) => (
                    <button
                      type="button"
                    key={tab} 
                    onClick={() => handleAddToList(tab)} 
                      className="list-selector-card"
                  >
                      <span className="list-selector-icon">{['✦', '◈', '◎', '⬢', '✺', '✹'][index % 6]}</span>
                      <span className="list-selector-name">{tab}</span>
                    </button>
                ))
              )}
              <button onClick={() => setShowListSelectorModal(false)} style={{ width: '100%', padding: '12px', background: '#f1f5f9', border: 'none', borderRadius: '12px', fontWeight: '800', marginTop: '16px', cursor: 'pointer' }}>Close</button>
           </div>
        </div>
      )}

      {/* Bulk SMS Input Modal */}
      {showBulkSmsModal && (
        <div className="modal-overlay" onClick={() => setShowBulkSmsModal(false)}>
           <div className="card-modal" onClick={e => e.stopPropagation()} style={{ padding: '16px', background: '#e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>Send Broadcast to {selectedItems.length} users</span>
                <button onClick={() => setShowBulkSmsModal(false)} style={{ background: 'none', border: 'none', fontSize: '16px', cursor: 'pointer', color: '#64748b' }}>✕</button>
              </div>

              {uploadProgress > 0 && (
                <div style={{ padding: '8px 16px', background: '#f0fdf4', marginBottom: '8px', borderRadius: '8px' }}>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#62b875' }}>Sending File ({uploadProgress}%)...</span>
                </div>
              )}

              <input type="file" ref={bulkMediaInputRef} accept="image/*,video/*" onChange={handleBulkMediaSelect} style={{ display: 'none' }} />
              <input type="file" ref={bulkFileInputRef} accept="*" onChange={handleBulkFileSelect} style={{ display: 'none' }} />

              <form onSubmit={handleBulkTextSubmit} className="chat-input-form" style={{ borderRadius: '16px', alignItems: 'flex-end', padding: '10px' }}>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', background: '#f0f2f5', borderRadius: '24px', padding: '0 12px', minHeight: '40px' }}>
                  <span style={{ cursor: 'pointer', fontSize: '18px', marginRight: '6px' }}>😊</span>
                  <textarea 
                    ref={bulkTextareaRef}
                    rows="1"
                    placeholder="Message" 
                    value={bulkText} 
                    onChange={e => { setBulkText(e.target.value); if(bulkTextareaRef.current){ bulkTextareaRef.current.style.height = '20px'; bulkTextareaRef.current.style.height = Math.min(bulkTextareaRef.current.scrollHeight, 120) + 'px'; } }} 
                    style={{ flex: 1, padding: '10px 0', background: 'none', border: 'none', color: '#0f172a', outline: 'none', fontSize: '14px', resize: 'none', maxHeight: '120px', overflowY: 'auto' }} 
                  />
                  <button type="button" onClick={() => bulkFileInputRef.current?.click()} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', paddingBottom: '2px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                  </button>
                  <button type="button" onClick={() => bulkMediaInputRef.current?.click()} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', paddingBottom: '2px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                  </button>
                </div>
                
                {bulkText.trim() ? (
                  <button type="submit" className="black-action-btn" disabled={isUploading}>
                    {isUploading ? <span style={{fontSize:'12px'}}>...</span> : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" style={{ transform: 'translateY(1px)' }}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>}
                  </button>
                ) : (
                  <button type="button" onClick={openVoiceModal} className="black-action-btn">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                  </button>
                )}
              </form>
           </div>
        </div>
      )}

      {/* Voice Recorder Bottom Sheet for Bulk SMS */}
      {showVoiceModal && (
        <div className="modal-overlay" style={{ alignItems: 'flex-end', padding: 0, zIndex: 400 }}>
          <div className="voice-card-bottom">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>Broadcast Voice Note</span>
              <button onClick={cancelVoiceModal} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#64748b', padding: '0 4px' }}>✕</button>
            </div>
            <div className={`voice-circle ${isRecording ? 'recording' : ''}`} onClick={handleCircleVoiceClick}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', height: '36px' }}>
                {waveBars.map((val, idx) => (
                  <div key={idx} style={{ width: '3px', height: `${val}px`, backgroundColor: isRecording ? '#ef4444' : '#62b875', borderRadius: '3px', transition: 'height 0.05s ease' }} />
                ))}
              </div>
            </div>
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '15px', fontWeight: '800', color: isRecording ? '#ef4444' : '#0f172a' }}>
                {isRecording ? `Recording... (${recordingTime}s)` : 'Tap to start record'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
              <button onClick={cancelVoiceModal} style={{ flex: 1, padding: '10px', background: '#f1f5f9', border: 'none', borderRadius: '12px', fontWeight: '700', color: '#64748b' }}>Cancel</button>
              <button onClick={() => { cancelVoiceModal(); setTimeout(openVoiceModal, 200); }} style={{ flex: 1, padding: '10px', background: '#e2f4e6', border: 'none', borderRadius: '12px', fontWeight: '700', color: '#15803d' }}>Re-record</button>
            </div>
          </div>
        </div>
      )}

      {/* মেইন ড্যাশবোর্ড স্ক্রিন */}
      {officialPopupPost && (
        <div className={`official-popup-layer ${showOfficialPopup ? 'visible' : 'leaving'}`}>
          <OfficialPostCard
            post={officialPopupPost}
            variant="popup"
            onViewed={markOfficialPostViewed}
            currentUid={currentUid}
            currentUserData={currentUserData}
          />
        </div>
      )}

       <div className="dash-shell">
        
        {/* Selection Top Bar */}
        {selectedItems.length > 0 ? (
          <div className="selection-top-bar">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button onClick={() => setSelectedItems([])} style={{ background: 'none', border: 'none', color: 'white', fontSize: '18px', cursor: 'pointer', padding: 0 }}>✕</button>
              <span style={{ fontWeight: '800', fontSize: '15px' }}>{selectedItems.length} Selected</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {customTabs.includes(activeTab) ? (
                <>
                  <button onClick={handleRemoveFromCustomList} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#ef4444' }} title="Remove from list">🗑️</button>
                  <button onClick={() => setShowBulkSmsModal(true)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'white' }} title="Send SMS">💬</button>
                </>
              ) : (
                <>
                  <button onClick={() => setShowListSelectorModal(true)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'white' }} title="Add to List">📑</button>
                  <button onClick={handlePinAction} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: 'white' }} title="Pin / Unpin">📌</button>
                  <button onClick={handleFavouriteAction} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: favouriteIds.includes(selectedItems[0].id) ? '#f59e0b' : 'white' }} title="Favourite">⭐</button>
                  <button onClick={handleDeleteAction} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#ef4444' }} title="Remove from Dashboard">🗑️</button>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="fixed-top-header">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h1 style={{ fontSize: '24px', fontWeight: '900', margin: 0, color: '#0f172a' }}>Nexora</h1>

              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <button onClick={() => handleNavigate('/profile')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2"><circle cx="12" cy="8" r="5"/><path d="M20 21a8 8 0 1 0-16 0"/></svg>
                </button>
                <button onClick={() => handleNavigate('/settings')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                </button>
              </div>
            </div>

            <div className="search-box-pill">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input 
                type="text" 
                placeholder="Ask Nexora AI or Search"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{ background: 'none', border: 'none', outline: 'none', fontSize: '14px', width: '100%', color: '#0f172a' }}
              />
            </div>
          </div>
        )}

        <div className="dashboard-story-rail" aria-label="Stories">
          {(() => {
            const ownStory = dashboardStories.find(story => story.isMine);
            const openOwn = () => handleNavigate('/story-maker', ownStory
              ? { state: { ownerId: ownStory.ownerKey, storyId: ownStory.id } }
              : { state: { compose: true } });
            return (
              <div className="dashboard-story-item">
                <button type="button" className="dashboard-story-button" onClick={openOwn} title={ownStory ? 'View your story' : 'Add a story'}>
                  <span className={`dashboard-story-avatar ${ownStory && !ownStory.isSeen ? 'unseen' : ''}`}>
                    <img src={currentUserData?.photoURL || '/logo192.png'} alt="Your profile" />
                  </span>
                  <span>Your story</span>
                </button>
                <button type="button" className="dashboard-story-compose" title="Create a story" aria-label="Create a story" onClick={() => handleNavigate('/story-maker', { state: { compose: true } })}><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg></button>
              </div>
            );
          })()}
          {dashboardStories.filter(story => !story.isMine).map(story => (
            <button type="button" className="dashboard-story-item dashboard-story-button" key={story.id} title={`View ${story.userName || 'user'}'s story`} onClick={() => handleNavigate('/story-maker', { state: { ownerId: story.ownerKey, storyId: story.id } })}>
              <span className={`dashboard-story-avatar ${story.isSeen ? 'seen' : 'unseen'}`}><img src={story.userPhoto || '/logo192.png'} alt="" /></span>
              <span>{story.userName || 'Nexora User'}</span>
            </button>
          ))}
        </div>

        <div className="filter-tabs-bar">
          {['All', 'Unread', 'Favourites', 'Group', 'Channel', ...customTabs].map(tab => (
            <button 
              key={tab} 
              onClick={() => {
                if (tab === 'Channel') {
                  setSelectedItems([]);
                  navigate('/channel');
                  return;
                }
                setActiveTab(tab);
                setSelectedItems([]);
              }}
              onContextMenu={(e) => { e.preventDefault(); handleTabTouchStart(tab); }}
              onTouchStart={() => handleTabTouchStart(tab)}
              onTouchEnd={handleTabTouchEnd}
              className={`tab-pill ${activeTab === tab ? 'active' : ''}`}
            >
              {tab}
            </button>
          ))}
          <button onClick={handleAddCustomTab} className="tab-pill" style={{ padding: '6px 14px', fontSize: '16px', fontWeight: '800' }}>+</button>
        </div>

        {officialBannerPost && (
          <div className="official-post-banner-wrap">
            <OfficialPostCard
              post={officialBannerPost}
              variant="banner"
              onViewed={markOfficialPostViewed}
              currentUid={currentUid}
              currentUserData={currentUserData}
            />
          </div>
        )}

        {/* ৩. চ্যাটলিস্ট */}
        <div className="scrollable-feed-area">
          {finalDisplayList.length === 0 && customTabs.includes(activeTab) && (
            <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px 20px', fontSize: '14px' }}>
              এই লিস্টটি একদম ফাঁকা! "All" লিস্টে গিয়ে যেকোনো চ্যাট লং প্রেস করে ধরে 📑 আইকনে ক্লিক করে এখানে অ্যাড করতে পারবেন।
            </div>
          )}

          {finalDisplayList.map((item) => {
            const isPinned = pinnedIds.includes(item.id);
            const isFav = favouriteIds.includes(item.id);
            const isSelected = selectedItems.find(i => i.id === item.id) ? true : false;
            const unreadCount = item.unreadCount && item.unreadCount[currentUid] ? item.unreadCount[currentUid] : 0;

            const isMyLastMsg = item.lastSenderId === currentUid;
            let statusIcon = null;
            if (isMyLastMsg && !item.isComm && !item.isGroup) {
              const targetUnread = item.unreadCount?.[item.otherUid] || 0;
              if (targetUnread === 0) {
                statusIcon = <span style={{ color: '#62b875', fontSize: '10px', marginRight: '4px' }}>🟢</span>; 
              } else if (item.isOnline) {
                statusIcon = <span style={{ color: '#15803d', fontWeight: '900', marginRight: '4px', fontSize: '12px' }}>✓✓</span>;
              } else {
                statusIcon = <span style={{ color: '#ef4444', fontWeight: '900', marginRight: '4px', fontSize: '12px' }}>✓</span>;
              }
            }

            if (item.isAdminUser) {
              return (
                <div 
                  key={item.id} 
                  className={`special-admin-wrapper ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleItemClick(item)}
                >
                  <div className="flip-inner">
                    <div className="flip-face flip-front">
                      <div className="card-border-box">
                        <div className="glass-card-inner">
                          <div className="avatar-wrapper anim-coin-flip">
                            <img src={item.photoURL || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150"} alt="Avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                          </div>
                          
                          <div style={{ flex: 1, overflow: 'hidden', paddingRight: '40px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <span style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {item.title}
                              </span>
                              <svg className="dynamic-shield" viewBox="0 0 100 100">
                                <path className="shield-color" d="M50 5 L90 20 L90 50 C90 75 50 95 50 95 C50 95 10 75 10 50 L10 20 Z"/>
                                <path d="M50 11 L84 24 L84 49 C84 70 50 87 50 87 C50 87 16 70 16 49 L16 24 Z" fill="none" stroke="white" strokeWidth="2.5" opacity="0.8"/>
                                <path d="M32 48 L45 61 L68 38" stroke="white" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center' }}>
                              {statusIcon} {item.subText}
                            </div>
                            <div className="status-marquee">
                              <div className="animated-text">{item.adminBio}</div>
                            </div>
                          </div>
                          <div className={item.isOnline ? 'blinking-admin-online' : 'blinking-admin-offline'}>Admin</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flip-face flip-back">
                      <div className="card-border-box">
                        <div className="glass-card-inner">
                          <div className="avatar-wrapper anim-coin-flip">
                            <img src={item.photoURL || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150"} alt="Avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                          </div>
                          <div style={{ flex: 1, overflow: 'hidden', paddingRight: '40px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <span style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {item.title}
                              </span>
                              <svg className="dynamic-shield" viewBox="0 0 100 100">
                                <path className="shield-color" d="M50 5 L90 20 L90 50 C90 75 50 95 50 95 C50 95 10 75 10 50 L10 20 Z"/>
                                <path d="M50 11 L84 24 L84 49 C84 70 50 87 50 87 C50 87 16 70 16 49 L16 24 Z" fill="none" stroke="white" strokeWidth="2.5" opacity="0.8"/>
                                <path d="M32 48 L45 61 L68 38" stroke="white" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center' }}>
                              {statusIcon} {item.subText}
                            </div>
                            <div className="status-marquee">
                              <div className="animated-text">{item.adminBio}</div>
                            </div>
                          </div>
                          <div className={item.isOnline ? 'blinking-admin-online' : 'blinking-admin-offline'}>Admin</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div 
                key={item.id} 
                className={`feed-row ${isSelected ? 'selected' : ''}`}
                onClick={() => handleItemClick(item)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1, overflow: 'hidden' }}>
                  
                  <div className={(item.isPremiumUser || item.isAdminUser) && !item.isComm && !item.isGroup ? 'anim-coin-flip' : ''} style={{ position: 'relative', width: '48px', height: '48px', flexShrink: 0, perspective: '800px' }}>
                    <img 
                      src={item.photoURL || item.iconURL || item.groupImage || (item.isComm || item.isGroup ? "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=200" : "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150")} 
                      alt="Avatar" 
                      style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} 
                    />
                    
                    {!item.isComm && !item.isGroup && item.otherUid !== currentUid && (
                      <div style={{
                        position: 'absolute',
                        bottom: '0px',
                        right: '2px',
                        width: '13px',
                        height: '13px',
                        borderRadius: '50%',
                        backgroundColor: item.isOnline ? '#22c55e' : '#ef4444',
                        border: '2px solid #ffffff'
                      }} />
                    )}
                  </div>

                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: item.isAdminUser ? '#15803d' : '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.title}</span>
                        {(item.isAdminUser || item.isPremiumUser) && (
                          <svg className="dynamic-shield" viewBox="0 0 100 100" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
                            <path className="shield-color" d="M50 5 L90 20 L90 50 C90 75 50 95 50 95 C50 95 10 75 10 50 L10 20 Z"/>
                            <path d="M50 11 L84 24 L84 49 C84 70 50 87 50 87 C50 87 16 70 16 49 L16 24 Z" fill="none" stroke="white" strokeWidth="2.5" opacity="0.8"/>
                            <path d="M32 48 L45 61 L68 38" stroke="white" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </h4>
                      {isPinned && <span style={{ fontSize: '11px' }}>📌</span>}
                      {isFav && <span style={{ fontSize: '11px' }}>⭐</span>}
                    </div>

                    <p style={{ margin: '3px 0 0', fontSize: '13px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center' }}>
                      {statusIcon}
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.subText}</span>
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', marginLeft: '10px' }}>
                  <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '500' }}>
                    {item.isComm || item.isGroup ? '' : (() => {
                      const timestamp = item.updatedAt?.toMillis?.()
                        || (item.updatedAt?.seconds ? item.updatedAt.seconds * 1000 : item.updatedAt);

                      if (!timestamp) return '';

                      const date = new Date(timestamp);
                      const now = new Date();

                      const isToday =
                        date.getDate() === now.getDate() &&
                        date.getMonth() === now.getMonth() &&
                        date.getFullYear() === now.getFullYear();

                      const isYesterday =
                        new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).toDateString() ===
                        date.toDateString();

                      if (isToday) {
                        return date.toLocaleTimeString([], {
                          hour: 'numeric',
                          minute: '2-digit'
                        });
                      }

                      if (isYesterday) {
                        return 'Yesterday';
                      }

                      return date.toLocaleDateString([], {
                        day: '2-digit',
                        month: 'short'
                      });
                    })()}
                  </span>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {item.isComm && <span className="comm-tag" style={{ color: '#16a34a', background: '#f0fdf4' }}>Community</span>}
                    {item.isGroup && <span className="comm-tag" style={{ color: '#0284c7', background: '#e0f2fe' }}>Group</span>}
                    {unreadCount > 0 && <div className="unread-badge">{unreadCount}</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
};

function OfficialPostCard({ post, variant = 'banner', onViewed, currentUid, currentUserData }) {
  const isMediaOnly = ['image', 'video', 'apk'].includes(post?.postType);
  const videoVolume = Math.max(0, Math.min(1, Number(post?.videoVolume ?? 1)));
  const videoMuted = post?.videoMuted !== false;
  const isVisualMedia = post?.postType === 'image' || post?.postType === 'video';
  const [comments, setComments] = useState([]);
  const [commentCount, setCommentCount] = useState(Number(post?.commentCount || 0));
  const [commentText, setCommentText] = useState('');
  const [reactionType, setReactionType] = useState('');
  const [reactionCount, setReactionCount] = useState(Number(post?.reactionCount || 0));
  const [showComments, setShowComments] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [showMediaDetails, setShowMediaDetails] = useState(true);
  const reactionPressTimerRef = useRef(null);
  const suppressReactionClickRef = useRef(false);
  const reactionWrapRef = useRef(null);
  const mediaDetailsTimerRef = useRef(null);

  useEffect(() => {
    if (post && onViewed) onViewed(post);
  }, [post, onViewed]);

  useEffect(() => {
    if (!isVisualMedia) return undefined;
    setShowMediaDetails(true);
    if (mediaDetailsTimerRef.current) clearTimeout(mediaDetailsTimerRef.current);
    mediaDetailsTimerRef.current = setTimeout(() => setShowMediaDetails(false), 6000);
    return () => {
      if (mediaDetailsTimerRef.current) clearTimeout(mediaDetailsTimerRef.current);
    };
  }, [post?.id, isVisualMedia]);

  useEffect(() => {
    setReactionCount(Number(post?.reactionCount || 0));
    setCommentCount(Number(post?.commentCount || 0));
    setReactionType('');
    if (!post?.id || !currentUid) return undefined;
    const reactionRef = doc(db, 'officialUpdates', post.id, 'reactions', currentUid);
    const unsubscribeReaction = onSnapshot(reactionRef, snapshot => setReactionType(snapshot.exists() ? (snapshot.data()?.type || 'like') : ''));
    return () => unsubscribeReaction();
  }, [post?.id, currentUid]);

  useEffect(() => {
    if (!post?.id || post.commentCount !== undefined) return undefined;
    getCountFromServer(collection(db, 'officialUpdates', post.id, 'comments'))
      .then(snapshot => setCommentCount(snapshot.data().count))
      .catch(() => {});
    return undefined;
  }, [post?.id, post?.commentCount]);

  useEffect(() => {
    if (!showComments || !post?.id) return undefined;
    const commentQuery = query(collection(db, 'officialUpdates', post.id, 'comments'), orderBy('createdAt', 'asc'), limit(100));
    return onSnapshot(commentQuery, snapshot => {
      setComments(snapshot.docs.map(item => ({ id: item.id, ...item.data() })));
    });
  }, [showComments, post?.id]);

  useEffect(() => {
    if (!showReactionPicker) return undefined;
    const hidePicker = event => {
      if (!reactionWrapRef.current?.contains(event.target)) setShowReactionPicker(false);
    };
    document.addEventListener('pointerdown', hidePicker);
    return () => document.removeEventListener('pointerdown', hidePicker);
  }, [showReactionPicker]);

  const setReaction = async type => {
    if (!currentUid || !post?.id || post.allowReactions === false) return;
    setShowReactionPicker(false);
    const previousType = reactionType;
    const previousCount = reactionCount;
    const nextType = previousType === type ? '' : type;
    setReactionType(nextType);
    if (!previousType && nextType) setReactionCount(previousCount + 1);
    if (previousType && !nextType) setReactionCount(Math.max(0, previousCount - 1));
    const reactionRef = doc(db, 'officialUpdates', post.id, 'reactions', currentUid);
    const postRef = doc(db, 'officialUpdates', post.id);
    try {
      await runTransaction(db, async transaction => {
        const [reactionSnapshot, postSnapshot] = await Promise.all([transaction.get(reactionRef), transaction.get(postRef)]);
        if (reactionSnapshot.exists()) {
          if (reactionSnapshot.data()?.type === type) {
            transaction.delete(reactionRef);
            transaction.update(postRef, { reactionCount: Math.max(0, Number(postSnapshot.data()?.reactionCount || 0) - 1) });
          } else {
            transaction.update(reactionRef, { type, updatedAt: serverTimestamp() });
          }
        } else {
          transaction.set(reactionRef, { uid: currentUid, type, createdAt: serverTimestamp() });
          transaction.update(postRef, { reactionCount: Number(postSnapshot.data()?.reactionCount || 0) + 1 });
        }
      });
    } catch (error) {
      setReactionType(previousType);
      setReactionCount(previousCount);
      console.debug('Reaction update skipped:', error?.message || error);
    }
  };

  const handleReactionPointerDown = () => {
    suppressReactionClickRef.current = false;
    reactionPressTimerRef.current = setTimeout(() => {
      suppressReactionClickRef.current = true;
      setShowReactionPicker(true);
    }, 550);
  };

  const handleReactionPointerUp = () => {
    if (reactionPressTimerRef.current) clearTimeout(reactionPressTimerRef.current);
  };

  const handleReactionClick = () => {
    if (suppressReactionClickRef.current) {
      suppressReactionClickRef.current = false;
      return;
    }
    setReaction('like');
  };

  const submitComment = async event => {
    event.preventDefault();
    const text = commentText.trim();
    if (!text || !currentUid || !post?.id || post.allowComments === false || currentUserData?.commentsBlocked === true) return;
    setCommentText('');
    const optimisticComment = {
      id: `pending-${Date.now()}`,
      uid: currentUid,
      authorName: currentUserData?.fullName || 'Nexora User',
      text,
    };
    setComments(previous => [...previous, optimisticComment]);
    setCommentCount(previous => previous + 1);
    try {
      const userSnapshot = await getDoc(doc(db, 'users', currentUid));
      if (userSnapshot.exists() && userSnapshot.data()?.commentsBlocked === true) {
        setCommentText(text);
        setComments(previous => previous.filter(comment => comment.id !== optimisticComment.id));
        setCommentCount(previous => Math.max(0, previous - 1));
        return;
      }
      await addDoc(collection(db, 'officialUpdates', post.id, 'comments'), {
        uid: currentUid,
        authorName: currentUserData?.fullName || 'Nexora User',
        text,
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'officialUpdates', post.id), { commentCount: increment(1) });
    } catch (error) {
      setCommentText(text);
      setComments(previous => previous.filter(comment => comment.id !== optimisticComment.id));
      setCommentCount(previous => Math.max(0, previous - 1));
      console.debug('Comment add skipped:', error?.message || error);
    }
  };

  const openDownload = (event) => {
    event.preventDefault();
    const url = normalizeOfficialDriveUrl(post?.downloadUrl);
    if (url) window.location.href = url;
  };

  return (
    <>
    <article className={`official-post-card ${variant === 'banner' ? 'banner-card' : ''} ${isVisualMedia ? 'media-post-card' : ''}`}>
      {isVisualMedia && post.mediaUrl && (
        <div className="official-media-stage" onClick={() => setShowMediaDetails(previous => !previous)}>
          {post.postType === 'image' ? (
            <img className="official-post-media official-post-image" src={post.mediaUrl} alt="Nexora Officials" />
          ) : (
            <video
              className="official-post-media official-post-video"
              src={post.mediaUrl}
              autoPlay
              muted={videoMuted}
              playsInline
              loop={false}
              onLoadedMetadata={event => {
                event.currentTarget.volume = videoVolume;
                event.currentTarget.muted = videoMuted;
                event.currentTarget.play().catch(() => {});
              }}
            />
          )}
          <div className={`official-media-top ${showMediaDetails ? 'visible' : 'hidden'}`}>
            <img className="official-post-logo" src={post?.profileImage || '/NexoraOfficials.png'} alt="Nexora Officials" />
            <div>
              <div className="official-post-name">{post?.authorName || 'Nexora Officials'}<span className="official-verified">✓</span></div>
              <div className="official-post-subs">Subscribers • {Number(post?.subscriberCount || 0).toLocaleString()}</div>
            </div>
          </div>
        </div>
      )}

      <div className={`official-post-body ${isVisualMedia ? 'media-post-body' : ''}`}>
        {!isVisualMedia && <div className="official-post-head">
          <img
            className="official-post-logo"
            src={post?.profileImage || '/NexoraOfficials.png'}
            alt="Nexora Officials"
          />
          <div style={{ minWidth: 0 }}>
            <div className="official-post-name">
              {post?.authorName || 'Nexora Officials'}
              <span className="official-verified">✓</span>
            </div>
            {!isMediaOnly && (
              <div className="official-post-subs">Subscribers • {Number(post?.subscriberCount || 0).toLocaleString()}</div>
            )}
          </div>
        </div>}

        {post?.text && (post?.postType === 'text' || isVisualMedia) && <div className="official-post-text">{post.text}</div>}
        {post?.postType === 'voice' && post.mediaUrl && (
          <audio className="official-post-audio" src={post.mediaUrl} controls preload="metadata" />
        )}
        {post?.postType === 'apk' && post.downloadUrl && (
          <div className="official-apk-box">
            <div className="official-apk-name">{post.appName || 'Nexora App'}</div>
            <a className="official-download-btn" href={normalizeOfficialDriveUrl(post.downloadUrl)} onClick={openDownload}>
              Download
            </a>
          </div>
        )}

        {(post.allowReactions !== false || post.allowComments !== false) && (
          <div className="official-post-interactions">
            {post.allowReactions !== false && (
              <div ref={reactionWrapRef} className="official-reaction-wrap">
                {showReactionPicker && <div className="official-reaction-picker">{Object.keys(reactionIcons).map(type => <button key={type} type="button" title={type} onClick={() => setReaction(type)}>{reactionIcons[type]}</button>)}</div>}
                <button type="button" className={`official-reaction-btn ${reactionType ? 'active' : ''}`} onPointerDown={handleReactionPointerDown} onPointerUp={handleReactionPointerUp} onPointerLeave={handleReactionPointerUp} onClick={handleReactionClick}>
                  {reactionIcons[reactionType || 'like']} <span>{reactionCount}</span>
                </button>
              </div>
            )}
            {post.allowComments !== false && (
              <button type="button" className="official-comment-open-btn" onClick={() => setShowComments(true)}>Comment <span>{commentCount}</span></button>
            )}
          </div>
        )}
      </div>
    </article>
    {showComments && <div className="official-comments-modal" role="dialog" aria-modal="true"><div className="official-comments-sheet"><div className="official-comments-header"><strong>Comments</strong><button type="button" onClick={() => setShowComments(false)}>×</button></div><form onSubmit={submitComment} className="official-comment-form official-comment-form-top"><input value={commentText} onChange={event => setCommentText(event.target.value)} placeholder={currentUserData?.commentsBlocked ? 'Comments are blocked for your account' : 'Write a comment...'} maxLength={500} disabled={currentUserData?.commentsBlocked === true} /><button type="submit" disabled={!commentText.trim() || currentUserData?.commentsBlocked === true}>Send</button></form><div className="official-comment-list official-comment-list-modal">{comments.length === 0 ? <div className="official-no-comments">No comments yet.</div> : comments.map(comment => <div key={comment.id} className="official-comment"><strong>{comment.authorName || 'Nexora User'}</strong><span>{comment.text}</span></div>)}</div></div></div>}
    </>
  );
}

const reactionIcons = { like: '👍', love: '❤️', care: '🤗', wow: '😮', sad: '😢', angry: '😡' };

const styles = {
  container: { height: '100dvh', width: '100vw', overflow: 'hidden', backgroundColor: '#fafafa', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }
};

export default Dashboard;